import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  formatDistanceToNow,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parse,
  startOfMonth,
  startOfWeek,
  addDays,
  differenceInHours,
} from "date-fns";
import {
  LogOut,
  Calendar,
  Clock,
  Phone,
  MessageSquare,
  FileText,
  ChevronDown,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Bell,
  Wallet,
  CheckCircle2,
  Save,
  Users,
  PlusCircle,
  Search,
  UserPlus,
  Ban,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { bookableServices } from "@/data/serviceCatalog";
import { fetchApi } from "@/lib/apiBase";
import { parseDurationMinutes } from "@/lib/duration";
import { invalidatePublicAvailabilitySettingsCache } from "@/lib/publicHours";
const ADMIN_DASHBOARD_CACHE_KEY = "dadadeluxe-admin-dashboard-cache-v2";
const ADMIN_DASHBOARD_SECONDARY_CACHE_KEY = "dadadeluxe-admin-dashboard-secondary-cache-v2";
const SECONDARY_CACHE_MAX_AGE_MS = 10 * 60 * 1000;
const DEFAULT_AVAILABILITY_SETTINGS = {
  min_notice_hours: 48,
  slot_interval_minutes: 30,
  max_advance_days: 120,
  weekly_hours: [
    { day_key: "monday", label: "Monday", enabled: true, start_time: "11:30 AM", end_time: "8:00 PM" },
    { day_key: "tuesday", label: "Tuesday", enabled: true, start_time: "11:30 AM", end_time: "8:00 PM" },
    { day_key: "wednesday", label: "Wednesday", enabled: true, start_time: "11:30 AM", end_time: "8:00 PM" },
    { day_key: "thursday", label: "Thursday", enabled: true, start_time: "11:30 AM", end_time: "8:00 PM" },
    { day_key: "friday", label: "Friday", enabled: true, start_time: "2:00 PM", end_time: "8:00 PM" },
    { day_key: "saturday", label: "Saturday", enabled: true, start_time: "10:00 AM", end_time: "5:00 PM" },
    { day_key: "sunday", label: "Sunday", enabled: false, start_time: "10:00 AM", end_time: "5:00 PM" },
  ],
};

const statusColors = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
  no_show: "bg-rose-100 text-rose-800",
};

const paymentColors = {
  deposit_paid: "bg-violet-100 text-violet-800",
  paid_in_full: "bg-emerald-100 text-emerald-800",
};

function parseBookingDateTime(booking) {
  try {
    return parse(
      `${booking.preferred_date} ${booking.preferred_time}`,
      "yyyy-MM-dd h:mm a",
      new Date()
    );
  } catch (e) {
    return null;
  }
}

function parseBookingDate(value) {
  try {
    return parse(String(value || ""), "yyyy-MM-dd", new Date());
  } catch (e) {
    return null;
  }
}

function clientKey(email, phone) {
  return `${email}|${phone}`;
}

function parseMoneyValue(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const match = String(value).match(/(\d+(?:\.\d{1,2})?)/);
  return match ? Number(match[1]) : null;
}

function isHairLengthAddOn(addOn) {
  const name = String(addOn?.name || "");
  const id = String(addOn?.id || "");
  const haystack = `${name} ${id}`.toLowerCase();
  const hasQuotedLength = /\b(1[89]|2\d|3\d|40)\s*"/.test(haystack);
  const hasBundleLength = /\b(1[89]|2\d|3\d|40)(-(1[89]|2\d|3\d|40)){2,3}\b/.test(haystack);
  const hasLengthId = /\b(1[89]|2\d|3\d|40)[-_]?(?:inch|inches)\b/.test(haystack);
  const hasBundlePlusUnit = /\b\d+\s*(1[89]|2\d|3\d|40)\s*"\s*\+/.test(haystack);
  return hasQuotedLength || hasBundleLength || hasLengthId || hasBundlePlusUnit;
}

function getBookingAddOnLabels(booking) {
  if (!Array.isArray(booking?.selected_addons)) return [];
  return booking.selected_addons
    .map((addOn) => String(addOn?.name || "").trim())
    .filter(Boolean);
}

function getFirstName(value) {
  const name = String(value || "").trim();
  if (!name) return "";
  return name.split(/\s+/)[0].toLowerCase();
}

function getDayStatusCounts(bookings) {
  return bookings.reduce(
    (acc, booking) => {
      const status = booking?.status || "pending";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    { pending: 0, confirmed: 0, completed: 0 }
  );
}

function readDashboardCache() {
  try {
    const raw = window.localStorage.getItem(ADMIN_DASHBOARD_CACHE_KEY);
    if (!raw) return null;
    if (raw.length > 750000) {
      window.localStorage.removeItem(ADMIN_DASHBOARD_CACHE_KEY);
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (error) {
    return null;
  }
}

function readSecondaryDashboardCache() {
  try {
    const raw = window.localStorage.getItem(ADMIN_DASHBOARD_SECONDARY_CACHE_KEY);
    if (!raw) return null;
    if (raw.length > 750000) {
      window.localStorage.removeItem(ADMIN_DASHBOARD_SECONDARY_CACHE_KEY);
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const cachedAt = new Date(parsed.cached_at || 0).getTime();
    if (!cachedAt || Number.isNaN(cachedAt) || Date.now() - cachedAt > SECONDARY_CACHE_MAX_AGE_MS) {
      window.localStorage.removeItem(ADMIN_DASHBOARD_SECONDARY_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
}

function writeDashboardCache(payload) {
  try {
    window.localStorage.setItem(
      ADMIN_DASHBOARD_CACHE_KEY,
      JSON.stringify({
        bookings: Array.isArray(payload?.bookings) ? payload.bookings : [],
        availability_blocks: Array.isArray(payload?.availability_blocks) ? payload.availability_blocks : [],
        availability_settings: payload?.availability_settings || DEFAULT_AVAILABILITY_SETTINGS,
        site_settings: payload?.site_settings || null,
        cached_at: new Date().toISOString(),
      })
    );
  } catch (error) {
    // Ignore cache write failures.
  }
}

function mergeDashboardCache(partialPayload) {
  const current = readDashboardCache() || {};
  const next = {
    ...current,
    ...partialPayload,
  };
  writeDashboardCache(next);
}

function writeSecondaryDashboardCache(payload) {
  try {
    window.localStorage.setItem(
      ADMIN_DASHBOARD_SECONDARY_CACHE_KEY,
      JSON.stringify({
        contacts: Array.isArray(payload?.contacts) ? payload.contacts : [],
        clients: Array.isArray(payload?.clients) ? payload.clients : [],
        notes: Array.isArray(payload?.notes) ? payload.notes : [],
        cached_at: new Date().toISOString(),
      })
    );
  } catch (error) {
    // Ignore cache write failures.
  }
}

function mergeSecondaryDashboardCache(partialPayload) {
  const current = readSecondaryDashboardCache() || {};
  writeSecondaryDashboardCache({
    ...current,
    ...partialPayload,
  });
}

function isTransientFetchError(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.name === "TypeError" ||
    error?.name === "AbortError" ||
    message.includes("load failed") ||
    message.includes("failed to fetch") ||
    message.includes("networkerror")
  );
}

function delay(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function detectMobileDashboardViewport() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia("(max-width: 767px)").matches;
}

function getMobileDashboardWindow(monthDate) {
  const start = startOfMonth(addMonths(monthDate, -1));
  const end = endOfMonth(addMonths(monthDate, 1));
  return {
    startDate: format(start, "yyyy-MM-dd"),
    endDate: format(end, "yyyy-MM-dd"),
  };
}

function getMobileDashboardWindowKey(monthDate) {
  const { startDate, endDate } = getMobileDashboardWindow(monthDate);
  return `${startDate}:${endDate}`;
}

export default function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deletingContactId, setDeletingContactId] = useState(null);
  const [clients, setClients] = useState([]);
  const [availabilityBlocks, setAvailabilityBlocks] = useState([]);
  const [clientNotes, setClientNotes] = useState({});
  const [savingClientKey, setSavingClientKey] = useState(null);
  const [isSavingBlock, setIsSavingBlock] = useState(false);
  const [selectedBlockTime, setSelectedBlockTime] = useState("11:00 AM");
  const [availabilitySettings, setAvailabilitySettings] = useState(DEFAULT_AVAILABILITY_SETTINGS);
  const [siteSettings, setSiteSettings] = useState({ announcement_banner_text: "2026 April Calendar Now Open!" });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [isSavingAvailabilitySettings, setIsSavingAvailabilitySettings] = useState(false);
  const [isSavingSiteSettings, setIsSavingSiteSettings] = useState(false);
  const [activeTab, setActiveTab] = useState("appointments");
  const [isMobileViewport, setIsMobileViewport] = useState(() => detectMobileDashboardViewport());
  const [isPageVisible, setIsPageVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden"
  );
  const [hasCachedDashboard, setHasCachedDashboard] = useState(false);
  const [hasLoadedFullBookingDataset, setHasLoadedFullBookingDataset] = useState(false);
  const [loadedMobileWindowKey, setLoadedMobileWindowKey] = useState(null);
  const [expandedAgendaBooking, setExpandedAgendaBooking] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  const [draggedBookingId, setDraggedBookingId] = useState(null);
  const [showSubmark, setShowSubmark] = useState(true);
  const [manualRescheduleByBooking, setManualRescheduleByBooking] = useState({});
  const [balanceAdjustByBooking, setBalanceAdjustByBooking] = useState({});
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const [isLoadingManualTimes, setIsLoadingManualTimes] = useState(false);
  const [manualAvailableTimes, setManualAvailableTimes] = useState([]);
  const [manualSelectedAddOnIds, setManualSelectedAddOnIds] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [expandedClientCard, setExpandedClientCard] = useState(null);
  const [isClientComposerOpen, setIsClientComposerOpen] = useState(false);
  const [isSavingClientDirectory, setIsSavingClientDirectory] = useState(false);
  const [newClient, setNewClient] = useState({
    full_name: "",
    email: "",
    phone: "",
    id_document_name: "",
  });
  const [isAnnouncementOpen, setIsAnnouncementOpen] = useState(false);
  const [lastSeenAnnouncementAt, setLastSeenAnnouncementAt] = useState(null);
  const [manualBooking, setManualBooking] = useState({
    full_name: "",
    email: "",
    phone: "",
    service_id: "",
    preferred_date: "",
    preferred_time: "",
    notes: "",
  });
  const navigate = useNavigate();

  const token = localStorage.getItem("adminToken");
  const servicePriceByName = useMemo(() => {
    const map = new Map();
    bookableServices.forEach((service) => {
      map.set(String(service.name || "").trim().toLowerCase(), parseMoneyValue(service.price) || 0);
    });
    return map;
  }, []);

  const getBookingDisplayTotal = (booking) => {
    const estimated = parseMoneyValue(booking.estimated_total);
    if (estimated != null) return estimated;
    const normalizedName = String(booking.service_name || "").trim().toLowerCase();
    const base = servicePriceByName.get(normalizedName);
    if (base == null) return null;
    const addon = parseMoneyValue(booking.addon_amount) || 0;
    return base + addon;
  };

  const getBookingDisplayRemaining = (booking) => {
    const remaining = parseMoneyValue(booking.remaining_balance);
    if (remaining != null) return remaining;
    const total = getBookingDisplayTotal(booking);
    if (total == null) return null;
    const paymentStatus = booking.payment_status || "deposit_paid";
    if (paymentStatus === "paid_in_full") return 0;
    const deposit = parseMoneyValue(booking.deposit_amount) ?? 20;
    return Math.max(0, total - deposit);
  };

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const applyViewport = () => setIsMobileViewport(mediaQuery.matches);

    applyViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", applyViewport);
      return () => mediaQuery.removeEventListener("change", applyViewport);
    }

    mediaQuery.addListener(applyViewport);
    return () => mediaQuery.removeListener(applyViewport);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState !== "hidden");
    };

    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    const cachedDashboard = readDashboardCache();
    const cachedSecondaryDashboard = isMobileViewport ? null : readSecondaryDashboardCache();
    if (cachedDashboard) {
      setHasCachedDashboard(true);
      setBookings(Array.isArray(cachedDashboard.bookings) ? cachedDashboard.bookings : []);
      setAvailabilityBlocks(
        Array.isArray(cachedDashboard.availability_blocks) ? cachedDashboard.availability_blocks : []
      );
      setAvailabilitySettings(cachedDashboard.availability_settings || DEFAULT_AVAILABILITY_SETTINGS);
      if (cachedDashboard.site_settings) {
        setSiteSettings(cachedDashboard.site_settings);
      }
      setIsLoading(false);
      setIsLoadingContacts(false);
      setIsLoadingClients(false);
      setIsLoadingAvailability(false);
    }
    if (cachedSecondaryDashboard) {
      setHasCachedDashboard(true);
      setContacts(Array.isArray(cachedSecondaryDashboard.contacts) ? cachedSecondaryDashboard.contacts : []);
      setClients(Array.isArray(cachedSecondaryDashboard.clients) ? cachedSecondaryDashboard.clients : []);
      const cachedNoteMap = {};
      for (const note of cachedSecondaryDashboard.notes || []) {
        cachedNoteMap[clientKey(note.email, note.phone)] = note.note || "";
      }
      setClientNotes(cachedNoteMap);
      setIsLoadingContacts(false);
      setIsLoadingClients(false);
    }
    if (!cachedDashboard && !cachedSecondaryDashboard) {
      setHasCachedDashboard(false);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, isMobileViewport]);

  useEffect(() => {
    if (!token) return undefined;
    if (isMobileViewport || !isPageVisible) return undefined;
    const intervalId = window.setInterval(() => {
      fetchData({ silent: true });
    }, 90000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isMobileViewport, isPageVisible]);

  const handleUnauthorized = () => {
    localStorage.removeItem("adminToken");
    navigate("/");
  };

  const parseJson = async (response) => {
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error("Unauthorized");
    }
    if (!response.ok) {
      let detail = "";
      try {
        const errorBody = await response.json();
        detail = errorBody?.detail || "";
      } catch (e) {
        detail = "";
      }
      throw new Error(detail || `Request failed with status ${response.status}`);
    }
    return response.json();
  };

  const fetchWithRetry = async (path, options = {}, retryOptions = {}) => {
    const retries = retryOptions?.retries ?? 1;
    const retryDelayMs = retryOptions?.retryDelayMs ?? 600;
    let lastError = null;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await fetchApi(path, {
          cache: "no-store",
          ...options,
        });
      } catch (error) {
        lastError = error;
        if (attempt >= retries || !isTransientFetchError(error)) {
          throw error;
        }
        await delay(retryDelayMs);
      }
    }

    throw lastError || new Error("Request failed");
  };

  const applySecondaryDashboardData = (data) => {
    if (Array.isArray(data?.contacts)) {
      setContacts(data.contacts);
    }
    if (Array.isArray(data?.clients)) {
      setClients(data.clients);
    }
    if (Array.isArray(data?.notes)) {
      const noteMap = {};
      for (const note of data.notes) {
        noteMap[clientKey(note.email, note.phone)] = note.note || "";
      }
      setClientNotes(noteMap);
    }
  };

  const fetchSecondaryData = async (options = {}) => {
    const silent = options?.silent === true;
    if (!silent) {
      setIsLoadingContacts(true);
      setIsLoadingClients(true);
    }
    try {
      const [contactsRes, clientsRes, notesRes] = await Promise.all([
        fetchWithRetry("/contacts", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetchWithRetry("/clients", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetchWithRetry("/clients/notes", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const [contactsData, clientsData, notesData] = await Promise.all([
        parseJson(contactsRes),
        parseJson(clientsRes),
        parseJson(notesRes),
      ]);

      const nextData = {
        contacts: Array.isArray(contactsData) ? contactsData : [],
        clients: Array.isArray(clientsData) ? clientsData : [],
        notes: Array.isArray(notesData) ? notesData : [],
      };
      applySecondaryDashboardData(nextData);
      writeSecondaryDashboardCache(nextData);
      setHasCachedDashboard(true);
    } catch (error) {
      if (!silent) {
        if (isTransientFetchError(error) && hasCachedDashboard) {
          toast.error("Connection issue. Showing saved admin data.");
        } else {
          toast.error(error.message || "Failed to load client and contact data");
        }
      }
    } finally {
      if (!silent) {
        setIsLoadingContacts(false);
        setIsLoadingClients(false);
      }
    }
  };

  const fetchData = async (options = {}) => {
    const silent = options?.silent === true;
    const loadFullBookings = options?.loadFullBookings === true;
    if (!silent) {
      setIsLoading(true);
      setIsLoadingAvailability(true);
    }
    try {
      const shouldUseMobileBookingWindow = isMobileViewport && !loadFullBookings;
      const dashboardCoreParams = new URLSearchParams();
      let nextMobileWindowKey = null;
      if (shouldUseMobileBookingWindow) {
        const { startDate, endDate } = getMobileDashboardWindow(calendarMonth);
        dashboardCoreParams.set("start_date", startDate);
        dashboardCoreParams.set("end_date", endDate);
        dashboardCoreParams.set("limit", "180");
        nextMobileWindowKey = `${startDate}:${endDate}`;
      }

      const dashboardCorePath = dashboardCoreParams.size > 0
        ? `/admin/dashboard-core?${dashboardCoreParams.toString()}`
        : "/admin/dashboard-core";
      const dashboardCoreRes = await fetchWithRetry(dashboardCorePath, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let dashboardData;

      if (dashboardCoreRes.ok) {
        dashboardData = await dashboardCoreRes.json();
      } else if (dashboardCoreRes.status === 404) {
        const dashboardLegacyRes = await fetchWithRetry("/admin/dashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });
        dashboardData = await parseJson(dashboardLegacyRes);
        applySecondaryDashboardData(dashboardData);
      } else {
        dashboardData = await parseJson(dashboardCoreRes);
      }

      mergeDashboardCache(dashboardData);
      const incomingBookings = Array.isArray(dashboardData?.bookings) ? dashboardData.bookings : [];
      setBookings((prev) => {
        if (silent && prev.length > 0) {
          const previousIds = new Set(prev.map((booking) => booking.id));
          const newlyAdded = incomingBookings.filter((booking) => !previousIds.has(booking.id));
          if (newlyAdded.length > 0) {
            const latest = newlyAdded[0];
            toast.success(
              `${newlyAdded.length} new booking${newlyAdded.length > 1 ? "s" : ""}: ${latest.full_name} booked ${latest.service_name}`
            );
          }
        }
        return incomingBookings;
      });
      setAvailabilityBlocks(
        Array.isArray(dashboardData?.availability_blocks) ? dashboardData.availability_blocks : []
      );
      if (dashboardData?.availability_settings) {
        setAvailabilitySettings(dashboardData.availability_settings);
      }
      if (dashboardData?.site_settings) {
        setSiteSettings(dashboardData.site_settings);
      }
      setHasLoadedFullBookingDataset(!shouldUseMobileBookingWindow);
      if (shouldUseMobileBookingWindow) {
        setLoadedMobileWindowKey(nextMobileWindowKey);
      } else if (loadFullBookings) {
        setLoadedMobileWindowKey("FULL_DATASET");
      }
    } catch (error) {
      if (!silent) {
        if (isTransientFetchError(error) && hasCachedDashboard) {
          toast.error("Connection issue. Showing saved admin data.");
        } else {
          toast.error(error.message || "Failed to fetch data");
        }
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
        setIsLoadingAvailability(false);
      }
    }
  };

  useEffect(() => {
    if (!token) return;
    if (isMobileViewport) return;
    const timeoutId = window.setTimeout(() => {
      fetchSecondaryData({ silent: true });
    }, 1200);
    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isMobileViewport]);

  useEffect(() => {
    if (!token) return;
    if (activeTab === "clients" && clients.length === 0 && !isLoadingClients) {
      fetchSecondaryData({ silent: false });
    }
    if (activeTab === "contacts" && contacts.length === 0 && !isLoadingContacts) {
      fetchSecondaryData({ silent: false });
    }
    if (
      isMobileViewport &&
      activeTab === "revenue" &&
      !hasLoadedFullBookingDataset &&
      !isLoading
    ) {
      fetchData({ silent: false, loadFullBookings: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token, isMobileViewport, hasLoadedFullBookingDataset, isLoading]);

  useEffect(() => {
    if (!token || !isMobileViewport || activeTab !== "appointments" || isLoading) {
      return;
    }
    const nextWindowKey = getMobileDashboardWindowKey(calendarMonth);
    if (loadedMobileWindowKey === nextWindowKey || loadedMobileWindowKey === "FULL_DATASET") {
      return;
    }
    fetchData({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth, token, isMobileViewport, activeTab, isLoading, loadedMobileWindowKey]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) throw new Error("Failed to update");

      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: newStatus } : b))
      );
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updateBookingPayment = async (bookingId, newPaymentStatus) => {
    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}/payment`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ payment_status: newPaymentStatus }),
      });

      if (!res.ok) throw new Error("Failed to update payment");
      const updated = await res.json();

      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? updated : b))
      );
      toast.success("Payment updated");
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  const chargeRemainingBalance = async (bookingId) => {
    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}/charge-remaining`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to charge card");
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? data.booking : b))
      );
      toast.success("Remaining balance charged successfully");
    } catch (error) {
      toast.error(error.message || "Failed to charge remaining balance");
    }
  };

  const recalculateBookingBalance = async (bookingId) => {
    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}/balance`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: "recalculate" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to recalculate balance");
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? data : b)));
      toast.success("Balance recalculated");
    } catch (error) {
      toast.error(error.message || "Failed to recalculate balance");
    }
  };

  const addBookingAddon = async (bookingId) => {
    const rawAmount = balanceAdjustByBooking[bookingId]?.addonAmount;
    const amount = Number(rawAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid add-on amount");
      return;
    }
    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}/balance`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: "add_on", amount }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to add add-on");
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? data : b)));
      setBalanceAdjustByBooking((prev) => ({
        ...prev,
        [bookingId]: { ...(prev[bookingId] || {}), addonAmount: "" },
      }));
      toast.success("Add-on added to balance");
    } catch (error) {
      toast.error(error.message || "Failed to add add-on");
    }
  };

  const markReminderSent = async (bookingId, reminderType) => {
    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}/reminders`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reminder_type: reminderType, sent: true }),
      });
      if (!res.ok) throw new Error("Failed to update reminder");
      const updated = await res.json();
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? updated : b)));
      toast.success(`${reminderType} reminder marked sent`);
    } catch (error) {
      toast.error("Failed to update reminder");
    }
  };

  const rescheduleBooking = async (bookingId, newDate) => {
    const booking = bookings.find((b) => b.id === bookingId);
    if (!booking) return;

    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}/reschedule`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          preferred_date: format(newDate, "yyyy-MM-dd"),
          preferred_time: booking.preferred_time,
        }),
      });
      if (!res.ok) throw new Error("Failed to reschedule");
      const updated = await res.json();
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? updated : b)));
      toast.success("Booking rescheduled");
    } catch (error) {
      toast.error("Failed to reschedule booking");
    } finally {
      setDraggedBookingId(null);
    }
  };

  const rescheduleBookingManual = async (bookingId) => {
    const booking = bookings.find((b) => b.id === bookingId);
    if (!booking) return;

    const form = manualRescheduleByBooking[bookingId] || {};
    const preferredDate = form.preferred_date || booking.preferred_date;
    const preferredTime = form.preferred_time || booking.preferred_time;

    if (!preferredDate || !preferredTime) {
      toast.error("Please select both a date and time");
      return;
    }

    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}/reschedule`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          preferred_date: preferredDate,
          preferred_time: preferredTime,
        }),
      });
      if (!res.ok) throw new Error("Failed to reschedule");
      const updated = await res.json();
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? updated : b)));
      setManualRescheduleByBooking((prev) => ({
        ...prev,
        [bookingId]: {
          preferred_date: updated.preferred_date,
          preferred_time: updated.preferred_time,
        },
      }));
      toast.success("Appointment rescheduled");
    } catch (error) {
      toast.error("Failed to reschedule booking");
    }
  };

  const deleteBooking = async (bookingId) => {
    try {
      const res = await fetchWithRetry(`/bookings/${bookingId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to delete");

      setBookings((prev) => prev.filter((b) => b.id !== bookingId));
      toast.success("Booking deleted");
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const saveClientNote = async (email, phone) => {
    const key = clientKey(email, phone);
    setSavingClientKey(key);
    try {
      const res = await fetchWithRetry("/clients/notes", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          phone,
          note: clientNotes[key] || "",
        }),
      });
      if (!res.ok) throw new Error("Failed to save note");
      mergeSecondaryDashboardCache({
        notes: Object.entries({
          ...clientNotes,
          [key]: clientNotes[key] || "",
        }).map(([compoundKey, note]) => {
          const [cachedEmail = "", cachedPhone = ""] = compoundKey.split("|");
          return {
            email: cachedEmail,
            phone: cachedPhone,
            note,
          };
        }),
      });
      toast.success("Client note saved");
    } catch (error) {
      toast.error("Failed to save client note");
    } finally {
      setSavingClientKey(null);
    }
  };

  const createClientRecord = async () => {
    if (!newClient.full_name.trim() || !newClient.email.trim()) {
      toast.error("Enter at least the client's name and email");
      return;
    }

    setIsSavingClientDirectory(true);
    try {
      const res = await fetchWithRetry("/clients", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          full_name: newClient.full_name.trim(),
          email: newClient.email.trim().toLowerCase(),
          phone: newClient.phone.trim(),
          id_document_name: newClient.id_document_name.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to add client");

      const nextClients = [data, ...clients.filter((client) => client.id !== data.id)];
      setClients(nextClients);
      mergeSecondaryDashboardCache({ clients: nextClients });
      setExpandedClientCard(clientKey(data.email, data.phone));
      setIsClientComposerOpen(false);
      setNewClient({ full_name: "", email: "", phone: "", id_document_name: "" });
      toast.success("Client added");
    } catch (error) {
      toast.error(error.message || "Failed to add client");
    } finally {
      setIsSavingClientDirectory(false);
    }
  };

  const setClientBanStatus = async (client, shouldBan) => {
    const clientId = client.id || client.key;
    const existingReason = String(client.ban_reason || "").trim();
    let banReason = existingReason;

    if (shouldBan) {
      banReason = window.prompt("Reason for banning this client (optional)", existingReason) ?? existingReason;
    }

    setIsSavingClientDirectory(true);
    try {
      const res = await fetchWithRetry(`/clients/${encodeURIComponent(clientId)}/ban`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          is_banned: shouldBan,
          ban_reason: shouldBan ? banReason : "",
          email: client.email || "",
          phone: client.phone || "",
          full_name: client.name || "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Failed to ${shouldBan ? "ban" : "unban"} client`);

      setClients((prev) => {
        const exists = prev.some((entry) => entry.id === data.id);
        const nextClients = exists
          ? prev.map((entry) => (entry.id === data.id ? data : entry))
          : [data, ...prev];
        mergeSecondaryDashboardCache({ clients: nextClients });
        return nextClients;
      });
      toast.success(shouldBan ? "Client banned from booking" : "Client booking access restored");
    } catch (error) {
      toast.error(error.message || `Failed to ${shouldBan ? "ban" : "unban"} client`);
    } finally {
      setIsSavingClientDirectory(false);
    }
  };

  const deleteContactMessage = async (contact) => {
    setDeletingContactId(contact.id);
    try {
      const res = await fetchWithRetry(`/contacts/${encodeURIComponent(contact.id)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to delete message");

      const nextContacts = contacts.filter((entry) => entry.id !== contact.id);
      setContacts(nextContacts);
      mergeSecondaryDashboardCache({ contacts: nextContacts });
      toast.success("Message deleted");
    } catch (error) {
      toast.error(error.message || "Failed to delete message");
    } finally {
      setDeletingContactId(null);
    }
  };

  const deleteClientRecord = async (client) => {
    const clientId = client.id || client.key;
    setIsSavingClientDirectory(true);
    try {
      const res = await fetchWithRetry(`/clients/${encodeURIComponent(clientId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to delete client");

      const nextClients = clients.filter((entry) => entry.id !== clientId);
      setClients(nextClients);
      mergeSecondaryDashboardCache({ clients: nextClients });
      setExpandedClientCard((prev) => (prev === client.key ? null : prev));
      setClientNotes((prev) => {
        const next = { ...prev };
        delete next[client.key];
        mergeSecondaryDashboardCache({
          notes: Object.entries(next).map(([compoundKey, note]) => {
            const [cachedEmail = "", cachedPhone = ""] = compoundKey.split("|");
            return {
              email: cachedEmail,
              phone: cachedPhone,
              note,
            };
          }),
        });
        return next;
      });
      toast.success("Client removed");
    } catch (error) {
      toast.error(error.message || "Failed to delete client");
    } finally {
      setIsSavingClientDirectory(false);
    }
  };

  const selectedManualService = useMemo(
    () => bookableServices.find((s) => s.id === manualBooking.service_id || s.service_id === manualBooking.service_id) || null,
    [manualBooking.service_id]
  );
  const manualAvailableAddOns = selectedManualService?.addOns || [];
  const manualSelectedAddOns = manualAvailableAddOns.filter((addOn) =>
    manualSelectedAddOnIds.includes(addOn.id)
  );
  const manualAddonAmount = manualSelectedAddOns.reduce(
    (sum, addOn) => sum + Number(addOn.price || 0),
    0
  );
  const manualServiceBaseTotal = parseMoneyValue(selectedManualService?.price) || 0;
  const manualEstimatedTotal = manualServiceBaseTotal + manualAddonAmount;
  const manualDurationMinutes = Math.max(
    30,
    parseDurationMinutes(selectedManualService?.duration) +
      manualSelectedAddOns.reduce((sum, addOn) => sum + parseDurationMinutes(addOn?.name), 0)
  );
  const manualIsDeluxeService = /deluxe/i.test(selectedManualService?.name || "");
  const manualSelectedHairLengthTotal = manualSelectedAddOns.reduce((sum, addOn) => {
    const price = Number(addOn?.price || 0);
    if (manualIsDeluxeService && (isHairLengthAddOn(addOn) || price >= 200)) {
      return sum + price;
    }
    return sum;
  }, 0);
  const manualDueNow = Math.min(
    manualEstimatedTotal,
    manualIsDeluxeService
      ? manualServiceBaseTotal * 0.5 + manualSelectedHairLengthTotal * 0.5
      : 20
  );
  const manualRemaining = Math.max(manualEstimatedTotal - manualDueNow, 0);

  useEffect(() => {
    const run = async () => {
      if (!manualBooking.preferred_date) {
        setManualAvailableTimes([]);
        return;
      }
      setIsLoadingManualTimes(true);
      try {
        const res = await fetchWithRetry(
          `/available-times?date=${manualBooking.preferred_date}&duration_minutes=${manualDurationMinutes}`
        );
        const data = await res.json().catch(() => ({}));
        setManualAvailableTimes(Array.isArray(data.available_times) ? data.available_times : []);
      } catch (e) {
        setManualAvailableTimes([]);
      } finally {
        setIsLoadingManualTimes(false);
      }
    };
    run();
  }, [manualBooking.preferred_date, manualDurationMinutes]);

  const createManualBooking = async () => {
    if (!manualBooking.full_name || !manualBooking.email || !manualBooking.phone) {
      toast.error("Client name, email, and phone are required");
      return;
    }
    if (!manualBooking.service_id || !selectedManualService) {
      toast.error("Please select a service");
      return;
    }
    if (!manualBooking.preferred_date || !manualBooking.preferred_time) {
      toast.error("Please select date and time");
      return;
    }

    setIsCreatingBooking(true);
    try {
      const payload = {
        service_id: selectedManualService.service_id || selectedManualService.id,
        service_name: selectedManualService.name,
        service_price: selectedManualService.price,
        service_duration: selectedManualService.duration,
        duration_minutes: manualDurationMinutes,
        selected_addons: manualSelectedAddOns.map((addOn) => ({
          id: addOn.id,
          name: addOn.name,
          price: Number(addOn.price || 0),
          duration_minutes: parseDurationMinutes(addOn.name),
          note: "",
        })),
        addon_amount: manualAddonAmount,
        full_name: manualBooking.full_name,
        email: manualBooking.email,
        phone: manualBooking.phone,
        preferred_date: manualBooking.preferred_date,
        preferred_time: manualBooking.preferred_time,
        notes: manualBooking.notes,
        status: "confirmed",
        payment_status: "deposit_paid",
        deposit_amount: manualDueNow,
      };

      const res = await fetchWithRetry("/bookings/admin-create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to create appointment");
      toast.success("Appointment created");
      setBookings((prev) => [...prev, data]);
      setManualBooking({
        full_name: "",
        email: "",
        phone: "",
        service_id: "",
        preferred_date: "",
        preferred_time: "",
        notes: "",
      });
      setManualSelectedAddOnIds([]);
      setManualAvailableTimes([]);
    } catch (error) {
      toast.error(error.message || "Failed to create appointment");
    } finally {
      setIsCreatingBooking(false);
    }
  };

  const createAvailabilityBlock = async (payload) => {
    setIsSavingBlock(true);
    try {
      const res = await fetchWithRetry("/availability/blocks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to create availability block");
      const created = await res.json();
      setAvailabilityBlocks((prev) => [created, ...prev]);
      toast.success("Availability updated");
    } catch (error) {
      toast.error("Failed to update availability");
    } finally {
      setIsSavingBlock(false);
    }
  };

  const removeAvailabilityBlock = async (blockId) => {
    try {
      const res = await fetchWithRetry(`/availability/blocks/${blockId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete block");
      setAvailabilityBlocks((prev) => prev.filter((block) => block.id !== blockId));
      toast.success("Availability opened");
    } catch (error) {
      toast.error("Failed to remove block");
    }
  };

  const updateAvailabilityDay = (dayKey, updates) => {
    setAvailabilitySettings((prev) => ({
      ...prev,
      weekly_hours: prev.weekly_hours.map((day) =>
        day.day_key === dayKey ? { ...day, ...updates } : day
      ),
    }));
  };

  const saveAvailabilitySettings = async () => {
    setIsSavingAvailabilitySettings(true);
    try {
      const res = await fetchWithRetry("/availability/settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(availabilitySettings),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to save availability settings");
      setAvailabilitySettings(data);
      invalidatePublicAvailabilitySettingsCache();
      mergeDashboardCache({ availability_settings: data });
      toast.success("Availability settings updated");
    } catch (error) {
      toast.error(error.message || "Failed to update availability settings");
    } finally {
      setIsSavingAvailabilitySettings(false);
    }
  };

  const saveSiteSettings = async () => {
    setIsSavingSiteSettings(true);
    try {
      const res = await fetchWithRetry("/site-settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(siteSettings),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to save website settings");
      setSiteSettings(data);
      mergeDashboardCache({ site_settings: data });
      toast.success("Homepage banner updated");
    } catch (error) {
      toast.error(error.message || "Failed to update homepage banner");
    } finally {
      setIsSavingSiteSettings(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminEmail");
    navigate("/");
  };

  const now = useMemo(() => new Date(), []);
  const bookingsWithDate = useMemo(() => {
    return bookings
      .map((booking) => ({ ...booking, scheduledAt: parseBookingDateTime(booking) }))
      .sort((a, b) => {
        if (!a.scheduledAt || !b.scheduledAt) return 0;
        return a.scheduledAt - b.scheduledAt;
      });
  }, [bookings]);
  const recentBookingFeed = useMemo(() => {
    if (!isAnnouncementOpen) {
      return [];
    }
    return [...bookingsWithDate]
      .filter((booking) => booking.created_at)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 8);
  }, [bookingsWithDate, isAnnouncementOpen]);
  const latestAnnouncementCreatedAt = useMemo(() => {
    if (recentBookingFeed.length > 0) {
      return recentBookingFeed[0]?.created_at || null;
    }
    let latest = null;
    for (const booking of bookingsWithDate) {
      if (!booking.created_at) continue;
      if (!latest || new Date(booking.created_at).getTime() > new Date(latest).getTime()) {
        latest = booking.created_at;
      }
    }
    return latest;
  }, [bookingsWithDate, recentBookingFeed]);
  const recentBookingTimestamps = useMemo(() => {
    return bookingsWithDate
      .filter((booking) => booking.created_at)
      .map((booking) => new Date(booking.created_at).getTime())
      .filter((value) => Number.isFinite(value));
  }, [bookingsWithDate]);
  const unreadAnnouncementCount = useMemo(() => {
    if (!latestAnnouncementCreatedAt) return 0;
    const lastSeenTime = lastSeenAnnouncementAt ? new Date(lastSeenAnnouncementAt).getTime() : 0;
    return recentBookingTimestamps.filter((createdAt) => createdAt > lastSeenTime).length;
  }, [lastSeenAnnouncementAt, latestAnnouncementCreatedAt, recentBookingTimestamps]);

  useEffect(() => {
    if (!lastSeenAnnouncementAt && latestAnnouncementCreatedAt) {
      setLastSeenAnnouncementAt(latestAnnouncementCreatedAt);
    }
  }, [lastSeenAnnouncementAt, latestAnnouncementCreatedAt]);

  const todayBookings = bookingsWithDate.filter(
    (b) => b.scheduledAt && isSameDay(b.scheduledAt, now) && b.status !== "cancelled"
  );
  const upcomingBookings = bookingsWithDate.filter(
    (b) => b.scheduledAt && b.scheduledAt >= now && b.status !== "cancelled"
  );
  const pendingBookings = bookingsWithDate.filter((b) => b.status === "pending");
  const noShowBookings = bookingsWithDate.filter((b) => b.status === "no_show");
  const cancelledBookings = bookingsWithDate.filter((b) => b.status === "cancelled");

  const projectedRemaining = bookingsWithDate.reduce((sum, b) => {
    return sum + Number(b.remaining_balance || 0);
  }, 0);

  const cancellationRate = bookings.length
    ? ((cancelledBookings.length / bookings.length) * 100).toFixed(1)
    : "0.0";

  const bookingCollectedAmount = (booking) => {
    const deposit = Number(booking.deposit_amount ?? 0);
    const estimatedTotal = Number(booking.estimated_total ?? 0);
    const paymentStatus = booking.payment_status || "deposit_paid";
    if (paymentStatus === "paid_in_full") {
      return estimatedTotal > 0 ? estimatedTotal : deposit;
    }
    return deposit;
  };

  const activeBookings = bookingsWithDate.filter((b) => b.status !== "cancelled");
  const totalCollectedRevenue = activeBookings.reduce((sum, booking) => sum + bookingCollectedAmount(booking), 0);
  const totalProjectedRevenue = activeBookings.reduce(
    (sum, booking) => sum + Number(booking.estimated_total || 0),
    0
  );
  const outstandingRevenue = activeBookings.reduce(
    (sum, booking) => sum + Number(booking.remaining_balance || 0),
    0
  );
  const monthCollectedRevenue = activeBookings.reduce((sum, booking) => {
    if (!booking.scheduledAt) return sum;
    const sameMonth =
      booking.scheduledAt.getMonth() === now.getMonth() &&
      booking.scheduledAt.getFullYear() === now.getFullYear();
    return sameMonth ? sum + bookingCollectedAmount(booking) : sum;
  }, 0);

  const stats = {
    total: bookings.length,
    pending: pendingBookings.length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    completed: bookings.filter((b) => b.status === "completed").length,
    today: todayBookings.length,
    clients: new Set(bookings.map((b) => `${b.full_name}|${b.email}|${b.phone}`)).size,
  };

  const monthStart = startOfMonth(calendarMonth);
  const monthEnd = endOfMonth(calendarMonth);
  const calendarGridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarGridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = useMemo(() => {
    const days = [];
    let dayCursor = calendarGridStart;
    while (dayCursor <= calendarGridEnd) {
      days.push(dayCursor);
      dayCursor = addDays(dayCursor, 1);
    }
    return days;
  }, [calendarGridStart, calendarGridEnd]);

  const bookingsByDay = useMemo(() => {
    const grouped = {};
    for (const booking of bookingsWithDate) {
      if (!booking.scheduledAt || booking.status === "cancelled") continue;
      const key = format(booking.scheduledAt, "yyyy-MM-dd");
      grouped[key] = (grouped[key] || 0) + 1;
    }
    return grouped;
  }, [bookingsWithDate]);

  const safeAvailabilityBlocks = useMemo(
    () => (Array.isArray(availabilityBlocks) ? availabilityBlocks : []),
    [availabilityBlocks]
  );

  const blockedByDay = useMemo(() => {
    const blocked = {};
    for (const block of safeAvailabilityBlocks) {
      if (block.block_type === "day" && block.date) {
        blocked[block.date] = true;
      } else if (block.block_type === "month" && block.month) {
        for (const day of calendarDays) {
          const key = format(day, "yyyy-MM-dd");
          if (key.startsWith(block.month)) blocked[key] = true;
        }
      } else if (block.block_type === "week" && block.start_date && block.end_date) {
        for (const day of calendarDays) {
          const key = format(day, "yyyy-MM-dd");
          if (key >= block.start_date && key <= block.end_date) blocked[key] = true;
        }
      }
    }
    return blocked;
  }, [safeAvailabilityBlocks, calendarDays]);

  const selectedDayLabel = format(selectedCalendarDate, "yyyy-MM-dd");
  const selectedWeekStart = format(startOfWeek(selectedCalendarDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
  const selectedWeekEnd = format(endOfWeek(selectedCalendarDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
  const selectedMonth = format(selectedCalendarDate, "yyyy-MM");
  const timeSlots = ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];
  const operatingTimeOptions = [
    "6:00 AM", "6:30 AM", "7:00 AM", "7:30 AM", "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM",
    "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
    "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM",
    "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM", "8:30 PM", "9:00 PM",
  ];
  const adminTimeSlots = [
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
    "6:00 PM",
    "7:00 PM",
    "8:00 PM",
  ];

  const selectedDayBookings = bookingsWithDate.filter(
    (b) => b.scheduledAt && isSameDay(b.scheduledAt, selectedCalendarDate)
  );
  const selectedDayStatusCounts = useMemo(
    () => getDayStatusCounts(selectedDayBookings),
    [selectedDayBookings]
  );

  const reminderQueue = bookingsWithDate.filter((b) => {
    if (!b.scheduledAt || !["pending", "confirmed"].includes(b.status)) return false;
    const hours = differenceInHours(b.scheduledAt, new Date());
    const needs24h = hours <= 24 && hours > 2 && !b.reminder_24h_sent;
    const needs2h = hours <= 2 && hours >= 0 && !b.reminder_2h_sent;
    return needs24h || needs2h;
  });

  const trackedClientCount = useMemo(() => {
    const uniqueClients = new Set();
    for (const booking of bookingsWithDate) {
      uniqueClients.add(clientKey(booking.email, booking.phone));
    }
    return uniqueClients.size;
  }, [bookingsWithDate]);

  const trackedClients = useMemo(() => {
    if (activeTab !== "clients") {
      return [];
    }

    const map = new Map();
    for (const booking of bookingsWithDate) {
      const key = clientKey(booking.email, booking.phone);
      const entry = map.get(key) || {
        key,
        name: booking.full_name,
        email: booking.email,
        phone: booking.phone,
        visits: 0,
        latest: null,
        upcoming: null,
        history: [],
      };
      entry.visits += 1;
      entry.history.push({
        id: booking.id,
        service: booking.service_name,
        date: booking.scheduledAt,
      });
      if (booking.scheduledAt && (!entry.latest || booking.scheduledAt > entry.latest)) {
        entry.latest = booking.scheduledAt;
      }
      if (
        booking.scheduledAt &&
        booking.scheduledAt >= new Date() &&
        (!entry.upcoming || booking.scheduledAt < entry.upcoming)
      ) {
        entry.upcoming = booking.scheduledAt;
      }
      map.set(key, entry);
    }

    return Array.from(map.values())
      .map((c) => ({
        ...c,
        history: c.history
          .filter((h) => h.date)
          .sort((a, b) => b.date - a.date)
          .slice(0, 4),
      }))
      .sort((a, b) => b.visits - a.visits);
  }, [activeTab, bookingsWithDate]);

  const clientBook = useMemo(() => {
    if (activeTab !== "clients") {
      return [];
    }

    const historyMap = new Map();
    for (const booking of bookingsWithDate) {
      const key = clientKey(booking.email, booking.phone);
      const list = historyMap.get(key) || [];
      list.push({ id: booking.id, service: booking.service_name, date: booking.scheduledAt });
      historyMap.set(key, list);
    }

    const mapped = (Array.isArray(clients) ? clients : []).map((client) => {
      const key = clientKey(client.email, client.phone);
      const history = (historyMap.get(key) || [])
        .filter((h) => h.date)
        .sort((a, b) => b.date - a.date)
        .slice(0, 6);
      return {
        id: client.id || key,
        key,
        is_persisted: true,
        name: client.full_name,
        email: client.email,
        phone: client.phone,
        visits: Number(client.total_visits || history.length || 0),
        latest: client.last_visit_at ? new Date(client.last_visit_at) : history[0]?.date || null,
        upcoming: client.next_appointment_at ? new Date(client.next_appointment_at) : null,
        id_document_name: client.id_document_name || "",
        id_document_data: client.id_document_data || "",
        is_banned: Boolean(client.is_banned),
        ban_reason: client.ban_reason || "",
        banned_at: client.banned_at ? new Date(client.banned_at) : null,
        created_at: client.created_at ? new Date(client.created_at) : null,
        history,
      };
    });

    const existingKeys = new Set(mapped.map((c) => c.key));
    for (const client of trackedClients) {
      if (!existingKeys.has(client.key)) {
        mapped.push({
          ...client,
          id: client.key,
          is_persisted: false,
          id_document_name: "",
          id_document_data: "",
          is_banned: false,
          ban_reason: "",
          banned_at: null,
          created_at: null,
        });
      }
    }

    return mapped.sort((a, b) => {
      const nameCompare = getFirstName(a.name).localeCompare(getFirstName(b.name));
      if (nameCompare !== 0) return nameCompare;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [activeTab, clients, bookingsWithDate, trackedClients]);

  const filteredClientBook = useMemo(() => {
    if (activeTab !== "clients") {
      return [];
    }

    const query = clientSearchQuery.trim().toLowerCase();
    if (!query) return clientBook;
    return clientBook.filter((client) => {
      const haystack = [
        client.name,
        client.email,
        client.phone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [activeTab, clientBook, clientSearchQuery]);

  const clientRecordCount = clients.length || trackedClientCount;

  const dashboardTabs = [
    { id: "appointments", label: "Appointments", meta: `${bookings.length} bookings` },
    { id: "revenue", label: "Revenue", meta: `${stats.completed} completed` },
    { id: "create", label: "Create Appointment", meta: "Manual booking" },
    { id: "clients", label: "Clients", meta: `${clientRecordCount} records` },
    { id: "contacts", label: "Messages", meta: `${contacts.length} inquiries` },
    { id: "availability", label: "Availability", meta: `${safeAvailabilityBlocks.length} blocks` },
    { id: "website", label: "Website", meta: "Banner controls" },
  ];
  const activeDashboardTab = dashboardTabs.find((tab) => tab.id === activeTab);

  if (!token) return null;

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#f9f4ed_0%,#f3f3f3_38%,#efefef_100%)]">
      <header className="bg-white/90 backdrop-blur-md border-b border-[#e8e8e8] sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {showSubmark && (
              <img
                src="/images/submark-logo.png"
                alt="DadaDeLuxe submark"
                className="h-9 w-auto shrink-0"
                onError={() => setShowSubmark(false)}
              />
            )}
            <div className="min-w-0">
              <p className="logo-text text-base md:text-lg text-[#111111] truncate">DADADELUXE</p>
              <p className="text-[10px] tracking-[0.22em] text-[#888] uppercase">Stylist Command Center</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="text-xs md:text-sm px-3 py-1.5 rounded-full border border-[#d8d8d8] text-[#555] hover:text-[#111] hover:border-[#bdbdbd] transition-colors"
            >
              Home
            </Link>
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsAnnouncementOpen((prev) => !prev);
                  if (!isAnnouncementOpen && latestAnnouncementCreatedAt) {
                    setLastSeenAnnouncementAt(latestAnnouncementCreatedAt);
                  }
                }}
                className="relative p-2 rounded-full border border-[#e4dbd2] bg-white hover:bg-[#faf7f2] transition-colors"
                title="Recent bookings"
                aria-label="Recent bookings"
              >
                <Bell size={18} />
                {unreadAnnouncementCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#111111] px-1 text-[10px] leading-[18px] text-white text-center">
                    {Math.min(unreadAnnouncementCount, 9)}
                  </span>
                )}
              </button>
              {isAnnouncementOpen && (
                <div className="fixed inset-x-3 top-[78px] z-[70] rounded-[24px] border border-[#e8dfd4] bg-[linear-gradient(180deg,rgba(255,251,246,0.98),rgba(255,255,255,0.98))] p-3 shadow-[0_24px_60px_rgba(17,17,17,0.15)] sm:absolute sm:right-0 sm:top-auto sm:inset-x-auto sm:mt-3 sm:w-[390px]">
                  <div className="flex items-center justify-between gap-3 px-2 pb-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.22em] text-[#8f7d69]">Live Booking Feed</p>
                      <p className="text-sm text-[#555] mt-1">Newest bookings and service requests</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setLastSeenAnnouncementAt(latestAnnouncementCreatedAt);
                        fetchData({ silent: true });
                      }}
                      className="text-xs rounded-full border border-[#ddd] px-3 py-1.5 hover:bg-white"
                    >
                      Refresh
                    </button>
                  </div>
                  <div className="max-h-[min(62vh,420px)] overflow-y-auto space-y-2 pr-1">
                    {recentBookingFeed.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-[#ddd] bg-white/80 px-4 py-8 text-center text-sm text-[#888]">
                        No recent bookings yet.
                      </div>
                    ) : (
                      recentBookingFeed.map((booking) => {
                        const addOnLabels = getBookingAddOnLabels(booking);
                        const createdLabel = booking.created_at
                          ? formatDistanceToNow(new Date(booking.created_at), { addSuffix: true })
                          : "recently";
                        return (
                          <button
                            key={`feed-${booking.id}`}
                            type="button"
                            onClick={() => {
                              const bookingDate = parseBookingDate(booking.preferred_date);
                              setActiveTab("appointments");
                              if (bookingDate) {
                                setSelectedCalendarDate(bookingDate);
                                setCalendarMonth(bookingDate);
                              }
                              setExpandedAgendaBooking(booking.id);
                              setIsAnnouncementOpen(false);
                              setLastSeenAnnouncementAt(latestAnnouncementCreatedAt);
                            }}
                            className="w-full rounded-2xl border border-[#ece4da] bg-white px-3 py-3 text-left hover:bg-[#fcfaf7] transition-colors"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-[#111111] truncate">{booking.full_name}</p>
                                <p className="text-xs text-[#8a7763] mt-1 truncate">{booking.service_name}</p>
                              </div>
                              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] ${statusColors[booking.status] || statusColors.pending}`}>
                                {booking.status || "pending"}
                              </span>
                            </div>
                            <p className="mt-2 text-xs text-[#666]">
                              {booking.preferred_date} • {booking.preferred_time} • {createdLabel}
                            </p>
                            <div className="mt-2 grid gap-2 sm:grid-cols-2 text-xs text-[#555]">
                              <div className="rounded-xl bg-[#faf7f2] px-3 py-2">
                                <p className="uppercase tracking-wide text-[10px] text-[#9b866f] mb-1">Contact</p>
                                <p className="truncate">{booking.phone || "No phone"}</p>
                                <p className="truncate">{booking.email || "No email"}</p>
                              </div>
                              <div className="rounded-xl bg-[#faf7f2] px-3 py-2">
                                <p className="uppercase tracking-wide text-[10px] text-[#9b866f] mb-1">Requested</p>
                                <p className="line-clamp-2">
                                  {addOnLabels.length > 0 ? addOnLabels.join(", ") : "No add-ons selected"}
                                </p>
                              </div>
                            </div>
                            {(booking.notes || addOnLabels.length > 0) && (
                              <div className="mt-2 rounded-xl border border-[#f0e6db] bg-[#fffdf9] px-3 py-2 text-xs text-[#5c5043]">
                                {booking.notes ? (
                                  <p className="line-clamp-2"><span className="text-[#8f7d69]">Notes:</span> {booking.notes}</p>
                                ) : (
                                  <p className="line-clamp-2"><span className="text-[#8f7d69]">Add-ons:</span> {addOnLabels.join(", ")}</p>
                                )}
                              </div>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              data-testid="refresh-btn"
              onClick={() => fetchData()}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-[#666] hover:text-[#111] transition-colors px-2"
            >
              <LogOut size={17} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <section className="grid gap-4 xl:grid-cols-[1.5fr_1fr] mb-6">
          <div className="overflow-hidden rounded-[28px] border border-[#eadfd4] bg-[linear-gradient(135deg,rgba(17,17,17,0.96),rgba(68,55,43,0.92))] px-5 py-5 text-white shadow-[0_20px_50px_rgba(17,17,17,0.12)] sm:px-6">
            <p className="text-[11px] uppercase tracking-[0.24em] text-white/60 mb-3">Admin Dashboard</p>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <h1 className="text-2xl sm:text-[2rem] leading-tight font-semibold">
                  {activeDashboardTab?.label || "Dashboard"}
                </h1>
                <p className="mt-2 text-sm sm:text-[15px] text-white/70">
                  A cleaner laptop-first workspace for bookings, client records, availability, and daily follow-up.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs sm:min-w-[260px]">
                <div className="rounded-2xl border border-white/10 bg-white/8 px-3 py-3">
                  <p className="uppercase tracking-wide text-white/50">Today</p>
                  <p className="mt-1 text-lg font-semibold">{format(new Date(), "EEE, MMM d")}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/8 px-3 py-3">
                  <p className="uppercase tracking-wide text-white/50">Focus</p>
                  <p className="mt-1 text-lg font-semibold">{activeDashboardTab?.meta || "Overview"}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card p-3 sm:p-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#8f7d69] mb-3">Workspace Sections</p>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
              {dashboardTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={tab.id === "appointments" ? "tab-bookings" : tab.id === "contacts" ? "tab-contacts" : undefined}
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                    activeTab === tab.id
                      ? "border-[#111111] bg-[#111111] text-white shadow-[0_12px_30px_rgba(17,17,17,0.12)]"
                      : "border-[#e8ddd1] bg-[linear-gradient(135deg,#fffdfb,#f7f1ea)] text-[#111111] hover:border-[#cdb8a3] hover:bg-white"
                  }`}
                >
                  <p className="text-sm font-medium">{tab.label}</p>
                  <p className={`mt-1 text-xs ${activeTab === tab.id ? "text-white/65" : "text-[#7b6b5c]"}`}>{tab.meta}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {activeTab === "revenue" && (
        <>
        <div data-testid="admin-stats" className="grid grid-cols-2 xl:grid-cols-12 gap-3 mb-6">
          <div className="glass-card p-4 xl:col-span-2">
            <p className="text-[11px] text-[#888] uppercase tracking-wide">Today</p>
            <p className="text-2xl font-semibold">{stats.today}</p>
          </div>
          <div className="glass-card p-4 xl:col-span-2">
            <p className="text-[11px] text-[#888] uppercase tracking-wide">Pending</p>
            <p className="text-2xl font-semibold">{stats.pending}</p>
          </div>
          <div className="glass-card p-4 xl:col-span-2">
            <p className="text-[11px] text-[#888] uppercase tracking-wide">Confirmed</p>
            <p className="text-2xl font-semibold">{stats.confirmed}</p>
          </div>
          <div className="glass-card p-4 xl:col-span-2">
            <p className="text-[11px] text-[#888] uppercase tracking-wide">Completed</p>
            <p className="text-2xl font-semibold">{stats.completed}</p>
          </div>
          <div className="glass-card p-4 xl:col-span-2">
            <p className="text-[11px] text-[#888] uppercase tracking-wide">Clients</p>
            <p className="text-2xl font-semibold">{stats.clients}</p>
          </div>
          <div className="glass-card p-4 xl:col-span-2 bg-[linear-gradient(135deg,#fff9f2,#f2e9dc)]">
            <p className="text-[11px] text-[#888] uppercase tracking-wide">Balance Due</p>
            <p className="text-2xl font-semibold">${projectedRemaining.toFixed(0)}</p>
          </div>
        </div>
        </>
        )}

        {(activeTab === "appointments" || activeTab === "availability") && (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(460px,0.95fr)] 2xl:grid-cols-[minmax(0,1.55fr)_minmax(520px,1fr)] mb-7 items-start">
          <section className="glass-card p-4 min-w-0">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-medium text-[#111] flex items-center gap-2">
                <Calendar size={16} />
                Schedule Calendar
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCalendarMonth(addMonths(calendarMonth, -1))}
                  className="p-1.5 rounded-full hover:bg-[#efefef]"
                  aria-label="Previous month"
                >
                  <ChevronLeft size={16} />
                </button>
                <p className="text-sm min-w-[130px] text-center">{format(monthStart, "MMMM yyyy")}</p>
                <button
                  onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                  className="p-1.5 rounded-full hover:bg-[#efefef]"
                  aria-label="Next month"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-wider text-[#888] mb-1">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} className="py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day) => {
                const key = format(day, "yyyy-MM-dd");
                const count = bookingsByDay[key] || 0;
                const isBlocked = !!blockedByDay[key];
                const selected = isSameDay(day, selectedCalendarDate);
                const inMonth = isSameMonth(day, monthStart);
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedCalendarDate(day)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (draggedBookingId) rescheduleBooking(draggedBookingId, day);
                    }}
                    className={`rounded-lg min-h-[56px] p-1.5 text-left border transition-all ${
                      selected
                        ? "bg-[#111111] text-white border-[#111111]"
                        : inMonth
                        ? "bg-white border-[#e6e6e6] hover:border-[#bcbcbc]"
                        : "bg-[#f7f7f7] text-[#aaa] border-[#efefef]"
                    }`}
                  >
                    <p className="text-xs">{format(day, "d")}</p>
                    {count > 0 && (
                      <p className={`text-[10px] mt-1 ${selected ? "text-white/90" : "text-[#444]"}`}>
                        {count} client{count > 1 ? "s" : ""}
                      </p>
                    )}
                    {isBlocked && (
                      <p className={`text-[10px] mt-1 ${selected ? "text-white/90" : "text-[#7a0000]"}`}>
                        Blocked
                      </p>
                    )}
                    {isToday(day) && (
                      <div className={`w-1.5 h-1.5 rounded-full mt-1 ${selected ? "bg-white" : "bg-[#111111]"}`} />
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#888] mt-2">
              Drag appointments from Upcoming Appointments and drop onto a day to reschedule.
            </p>
          </section>

          <section className="glass-card p-4 bg-[linear-gradient(180deg,rgba(255,250,244,0.9),rgba(255,255,255,0.98))] min-w-0 xl:sticky xl:top-28">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#8f7d69] mb-2">Selected Day Schedule</p>
                <h2 className="font-medium text-[#111] flex items-center gap-2">
                  <Bell size={16} />
                  {format(selectedCalendarDate, "EEEE, MMMM d")}
                </h2>
                <p className="text-xs text-[#888] mt-1">
                  {selectedDayBookings.length === 0
                    ? "No clients booked for this day."
                    : `${selectedDayBookings.length} appointment${selectedDayBookings.length > 1 ? "s" : ""} selected`}
                </p>
              </div>
              <div className="hidden sm:flex h-12 w-12 items-center justify-center rounded-2xl bg-[#111111] text-white text-sm font-semibold shadow-[0_12px_24px_rgba(17,17,17,0.18)]">
                {format(selectedCalendarDate, "d")}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="rounded-2xl border border-[#eadfd4] bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-[#a1886f]">Pending</p>
                <p className="text-lg font-semibold text-[#111111]">{selectedDayStatusCounts.pending || 0}</p>
              </div>
              <div className="rounded-2xl border border-[#eadfd4] bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-[#a1886f]">Confirmed</p>
                <p className="text-lg font-semibold text-[#111111]">{selectedDayStatusCounts.confirmed || 0}</p>
              </div>
              <div className="rounded-2xl border border-[#eadfd4] bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-[#a1886f]">Completed</p>
                <p className="text-lg font-semibold text-[#111111]">{selectedDayStatusCounts.completed || 0}</p>
              </div>
            </div>

            <div className="space-y-3 max-h-[780px] overflow-y-auto pr-1">
              {selectedDayBookings.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#ddd] bg-white/80 px-4 py-8 text-center">
                  <p className="text-sm text-[#888]">No appointments for this day.</p>
                </div>
              ) : (
                selectedDayBookings.map((booking) => {
                  const addOnLabels = getBookingAddOnLabels(booking);
                  const isExpanded = expandedAgendaBooking === booking.id;
                  return (
                    <div
                      key={booking.id}
                      className="overflow-hidden rounded-3xl border border-[#e8dfd4] bg-white shadow-[0_14px_40px_rgba(17,17,17,0.05)]"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedAgendaBooking(isExpanded ? null : booking.id)
                        }
                        className="w-full px-4 py-4 text-left transition-colors hover:bg-[#fcfaf7]"
                      >
                        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs ${statusColors[booking.status]}`}>
                                {booking.status}
                              </span>
                              <span className={`px-3 py-1 rounded-full text-xs ${paymentColors[booking.payment_status || "deposit_paid"]}`}>
                                {(booking.payment_status || "deposit_paid").replaceAll("_", " ")}
                              </span>
                            </div>
                            <p className="font-medium text-[#111111] text-base">{booking.full_name}</p>
                            <p className="text-sm text-[#666]">{booking.service_name}</p>
                            <p className="text-xs text-[#999] mt-1">
                              {parseBookingDate(booking.preferred_date)
                                ? format(parseBookingDate(booking.preferred_date), "MMM d, yyyy")
                                : booking.preferred_date}{" "}
                              • {booking.preferred_time}
                            </p>
                            {addOnLabels.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {addOnLabels.slice(0, 3).map((label) => (
                                  <span
                                    key={`${booking.id}-${label}`}
                                    className="rounded-full bg-[#f5efe7] px-2.5 py-1 text-[10px] text-[#6a5643]"
                                  >
                                    {label}
                                  </span>
                                ))}
                                {addOnLabels.length > 3 && (
                                  <span className="rounded-full bg-[#f5efe7] px-2.5 py-1 text-[10px] text-[#6a5643]">
                                    +{addOnLabels.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between gap-3 2xl:justify-end">
                            <div className="grid grid-cols-2 gap-2 text-xs w-full max-w-[220px]">
                              <div className="rounded-2xl bg-[#faf7f2] px-3 py-2">
                                <p className="text-[#a1886f] uppercase tracking-wide text-[10px]">Total</p>
                                <p className="font-semibold text-[#111111]">
                                  {getBookingDisplayTotal(booking) != null ? `$${Number(getBookingDisplayTotal(booking)).toFixed(2)}` : "N/A"}
                                </p>
                              </div>
                              <div className="rounded-2xl bg-[#faf7f2] px-3 py-2">
                                <p className="text-[#a1886f] uppercase tracking-wide text-[10px]">Remaining</p>
                                <p className="font-semibold text-[#111111]">
                                  {getBookingDisplayRemaining(booking) != null ? `$${Number(getBookingDisplayRemaining(booking)).toFixed(2)}` : "N/A"}
                                </p>
                              </div>
                            </div>
                            <ChevronDown
                              className={`text-[#888] transition-transform ${isExpanded ? "rotate-180" : ""}`}
                              size={18}
                            />
                          </div>
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-[#f0e7dc] px-4 pb-4">
                          <div className="space-y-4 py-4">
                            <div className="space-y-4">
                              <div className="grid gap-3 text-sm">
                                <div className="rounded-2xl bg-[#faf7f2] p-3 space-y-2">
                                  <p className="flex items-center gap-2">
                                    <Calendar size={14} className="text-[#888]" />
                                    {parseBookingDate(booking.preferred_date)
                                      ? format(parseBookingDate(booking.preferred_date), "MMMM d, yyyy")
                                      : booking.preferred_date}
                                  </p>
                                  <p className="flex items-center gap-2"><Clock size={14} className="text-[#888]" />{booking.preferred_time}</p>
                                  <p className="flex items-center gap-2"><FileText size={14} className="text-[#888]" />Booking ID: {booking.id}</p>
                                  <p className="flex items-center gap-2"><Phone size={14} className="text-[#888]" />{booking.phone}</p>
                                  <p className="break-all text-[#555]">{booking.email}</p>
                                </div>
                                <div className="rounded-2xl bg-[#faf7f2] p-3 space-y-2 text-sm">
                                  <p><span className="text-[#888]">Deposit:</span> ${Number(booking.deposit_amount ?? 20).toFixed(2)}</p>
                                  <p><span className="text-[#888]">Total:</span> {getBookingDisplayTotal(booking) != null ? `$${Number(getBookingDisplayTotal(booking)).toFixed(2)}` : "N/A"}</p>
                                  <p><span className="text-[#888]">Add-ons:</span> ${Number(booking.addon_amount || 0).toFixed(2)}</p>
                                  <p><span className="text-[#888]">Remaining:</span> {getBookingDisplayRemaining(booking) != null ? `$${Number(getBookingDisplayRemaining(booking)).toFixed(2)}` : "N/A"}</p>
                                  <p>
                                    <span className="text-[#888]">Card:</span>{" "}
                                    {booking.stripe_card_brand && booking.stripe_card_last4
                                      ? `${String(booking.stripe_card_brand).toUpperCase()} •••• ${booking.stripe_card_last4}`
                                      : "No saved card"}
                                  </p>
                                </div>
                              </div>

                              <div className="rounded-2xl border border-[#ececec] bg-white p-3">
                                <p className="text-xs uppercase tracking-wide text-[#888] mb-2">Service Notes & Add-Ons</p>
                                <div className="space-y-2 text-sm text-[#555]">
                                  <p><span className="text-[#888]">Service:</span> {booking.service_name || "N/A"}</p>
                                  <p><span className="text-[#888]">Add-Ons:</span> {addOnLabels.length > 0 ? addOnLabels.join(", ") : "None"}</p>
                                  {booking.notes && (
                                    <p><span className="text-[#888]">Notes:</span> {booking.notes}</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="space-y-4">
                              <div className="rounded-2xl border border-[#ececec] bg-white p-3">
                                <p className="text-xs uppercase tracking-wide text-[#888] mb-2">Manual Reschedule</p>
                                <div className="grid gap-2">
                                  <div>
                                    <label className="text-[11px] text-[#888] block mb-1">Date</label>
                                    <input
                                      type="date"
                                      value={
                                        (manualRescheduleByBooking[booking.id]?.preferred_date) ||
                                        booking.preferred_date ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        setManualRescheduleByBooking((prev) => ({
                                          ...prev,
                                          [booking.id]: {
                                            preferred_date: e.target.value,
                                            preferred_time:
                                              prev[booking.id]?.preferred_time || booking.preferred_time || "",
                                          },
                                        }))
                                      }
                                      className="w-full h-9 rounded-lg border border-[#ddd] px-3 text-sm outline-none focus:border-[#bfa58a]"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[11px] text-[#888] block mb-1">Time</label>
                                    <Select
                                      value={
                                        (manualRescheduleByBooking[booking.id]?.preferred_time) ||
                                        booking.preferred_time ||
                                        adminTimeSlots[0]
                                      }
                                      onValueChange={(value) =>
                                        setManualRescheduleByBooking((prev) => ({
                                          ...prev,
                                          [booking.id]: {
                                            preferred_date:
                                              prev[booking.id]?.preferred_date || booking.preferred_date || "",
                                            preferred_time: value,
                                          },
                                        }))
                                      }
                                    >
                                      <SelectTrigger className="h-9">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {Array.from(new Set([booking.preferred_time, ...adminTimeSlots].filter(Boolean))).map((slot) => (
                                          <SelectItem key={slot} value={slot}>
                                            {slot}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <button
                                    onClick={() => rescheduleBookingManual(booking.id)}
                                    className="btn-black text-xs px-4 py-2.5 rounded-full"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>

                              <div className="rounded-2xl border border-[#ececec] bg-white p-3 space-y-3">
                                <div className="grid gap-3">
                                  <div className="space-y-1.5">
                                    <span className="text-xs text-[#888] block">Status</span>
                                    <Select value={booking.status} onValueChange={(value) => updateBookingStatus(booking.id, value)}>
                                      <SelectTrigger data-testid={`status-select-${booking.id}`} className="w-full h-9">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="confirmed">Confirmed</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                        <SelectItem value="cancelled">Cancelled</SelectItem>
                                        <SelectItem value="no_show">No Show</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  <div className="space-y-1.5">
                                    <span className="text-xs text-[#888] block">Payment</span>
                                    <Select
                                      value={booking.payment_status || "deposit_paid"}
                                      onValueChange={(value) => updateBookingPayment(booking.id, value)}
                                    >
                                      <SelectTrigger data-testid={`payment-select-${booking.id}`} className="w-full h-9">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                                        <SelectItem value="paid_in_full">Paid in Full</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>

                                <div className="grid gap-2">
                                  <button
                                    onClick={() => recalculateBookingBalance(booking.id)}
                                    className="px-3 py-2 rounded-full border border-[#ddd] text-xs hover:bg-[#f8f8f8]"
                                  >
                                    Recalculate
                                  </button>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="Add-on $"
                                    value={balanceAdjustByBooking[booking.id]?.addonAmount || ""}
                                    onChange={(e) =>
                                      setBalanceAdjustByBooking((prev) => ({
                                        ...prev,
                                        [booking.id]: {
                                          ...(prev[booking.id] || {}),
                                          addonAmount: e.target.value,
                                        },
                                      }))
                                    }
                                    className="h-10 w-full rounded-md border border-[#ddd] px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#111111]/10"
                                  />
                                  <button
                                    onClick={() => addBookingAddon(booking.id)}
                                    className="px-3 py-2 rounded-full border border-[#111111] text-xs hover:bg-[#111111] hover:text-white"
                                  >
                                    Add Add-On
                                  </button>

                                  {Number(getBookingDisplayRemaining(booking) || 0) > 0 && (
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <button className="px-3 py-2 rounded-full bg-[#111111] text-white text-xs hover:bg-[#2a2a2a]">
                                          Charge Remaining
                                        </button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Charge Remaining Balance?</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            This will charge{" "}
                                            <strong>${Number(getBookingDisplayRemaining(booking) || 0).toFixed(2)}</strong>{" "}
                                            to the saved card for {booking.full_name}.
                                            {booking.stripe_card_brand && booking.stripe_card_last4
                                              ? ` Card: ${String(booking.stripe_card_brand).toUpperCase()} •••• ${booking.stripe_card_last4}.`
                                              : " No saved card details are displayed."}
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction onClick={() => chargeRemainingBalance(booking.id)}>
                                            Charge ${Number(getBookingDisplayRemaining(booking) || 0).toFixed(2)}
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  )}

                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <button
                                        data-testid={`delete-booking-${booking.id}`}
                                        className="flex h-10 w-10 items-center justify-center text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Booking?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          This will permanently delete the booking for {booking.full_name}.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => deleteBooking(booking.id)}
                                          className="bg-red-500 hover:bg-red-600"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
        )}

        {activeTab === "revenue" && (
        <>
        <div className="grid md:grid-cols-3 gap-3 mb-6">
          <div className="glass-card p-4">
            <h3 className="font-medium flex items-center gap-2 mb-2"><MessageSquare size={15} />No-Show Analytics</h3>
            <p className="text-2xl font-semibold">{noShowBookings.length}</p>
            <p className="text-xs text-[#888]">Clients marked as no-show</p>
          </div>
          <div className="glass-card p-4">
            <h3 className="font-medium flex items-center gap-2 mb-2"><Trash2 size={15} />Cancellation Rate</h3>
            <p className="text-2xl font-semibold">{cancellationRate}%</p>
            <p className="text-xs text-[#888]">{cancelledBookings.length} cancelled out of {bookings.length}</p>
          </div>
          <div className="glass-card p-4">
            <h3 className="font-medium flex items-center gap-2 mb-2"><Bell size={15} />Reminder Queue</h3>
            <p className="text-2xl font-semibold">{reminderQueue.length}</p>
            <p className="text-xs text-[#888]">Bookings needing 24h/2h reminders</p>
          </div>
        </div>

        <section className="glass-card p-4 mb-6">
          <h3 className="font-medium mb-3">Revenue Overview</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl border border-[#ececec] bg-white p-3">
              <p className="text-[11px] uppercase tracking-wide text-[#888]">Collected (All Time)</p>
              <p className="text-2xl font-semibold mt-1">${totalCollectedRevenue.toFixed(2)}</p>
            </div>
            <div className="rounded-xl border border-[#ececec] bg-white p-3">
              <p className="text-[11px] uppercase tracking-wide text-[#888]">Collected (This Month)</p>
              <p className="text-2xl font-semibold mt-1">${monthCollectedRevenue.toFixed(2)}</p>
            </div>
            <div className="rounded-xl border border-[#ececec] bg-white p-3">
              <p className="text-[11px] uppercase tracking-wide text-[#888]">Projected Total</p>
              <p className="text-2xl font-semibold mt-1">${totalProjectedRevenue.toFixed(2)}</p>
            </div>
            <div className="rounded-xl border border-[#ececec] bg-white p-3">
              <p className="text-[11px] uppercase tracking-wide text-[#888]">Outstanding Balance</p>
              <p className="text-2xl font-semibold mt-1">${outstandingRevenue.toFixed(2)}</p>
            </div>
          </div>
        </section>

        {reminderQueue.length > 0 && (
          <section className="glass-card p-4 mb-6">
            <h3 className="font-medium mb-3">Reminders To Send</h3>
            <div className="space-y-2">
              {reminderQueue.slice(0, 8).map((booking) => {
                const hours = booking.scheduledAt ? differenceInHours(booking.scheduledAt, new Date()) : null;
                const needs24h = hours != null && hours <= 24 && hours > 2 && !booking.reminder_24h_sent;
                const needs2h = hours != null && hours <= 2 && hours >= 0 && !booking.reminder_2h_sent;
                return (
                  <div key={`rem-${booking.id}`} className="rounded-xl border border-[#ececec] bg-white p-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-sm">{booking.full_name}</p>
                      <p className="text-xs text-[#888]">
                        {booking.scheduledAt ? format(booking.scheduledAt, "EEE, MMM d • h:mm a") : booking.preferred_date}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {needs24h && (
                        <button
                          onClick={() => markReminderSent(booking.id, "24h")}
                          className="px-3 py-1.5 rounded-full bg-[#111111] text-white text-xs"
                        >
                          Mark 24h Sent
                        </button>
                      )}
                      {needs2h && (
                        <button
                          onClick={() => markReminderSent(booking.id, "2h")}
                          className="px-3 py-1.5 rounded-full bg-[#111111] text-white text-xs"
                        >
                          Mark 2h Sent
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        </>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="spinner border-[#111111]" style={{ borderTopColor: "#111" }} />
          </div>
        ) : activeTab === "create" ? (
          <div className="space-y-3">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <PlusCircle size={16} />
                <h3 className="font-medium">Create Appointment Manually</h3>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                <input value={manualBooking.full_name} onChange={(e) => setManualBooking((prev) => ({ ...prev, full_name: e.target.value }))} className="h-9 rounded-md border border-[#ddd] px-3 text-sm" placeholder="Client full name" />
                <input type="email" value={manualBooking.email} onChange={(e) => setManualBooking((prev) => ({ ...prev, email: e.target.value }))} className="h-9 rounded-md border border-[#ddd] px-3 text-sm" placeholder="Client email" />
                <input value={manualBooking.phone} onChange={(e) => setManualBooking((prev) => ({ ...prev, phone: e.target.value }))} className="h-9 rounded-md border border-[#ddd] px-3 text-sm" placeholder="Client phone" />
                <Select
                  value={manualBooking.service_id}
                  onValueChange={(value) => {
                    setManualBooking((prev) => ({ ...prev, service_id: value, preferred_time: "" }));
                    setManualSelectedAddOnIds([]);
                  }}
                >
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>{bookableServices.map((service) => (<SelectItem key={service.id} value={service.id}>{service.name} • {service.price}</SelectItem>))}</SelectContent>
                </Select>
                <input type="date" value={manualBooking.preferred_date} onChange={(e) => setManualBooking((prev) => ({ ...prev, preferred_date: e.target.value, preferred_time: "" }))} className="h-9 rounded-md border border-[#ddd] px-3 text-sm" />
                <Select value={manualBooking.preferred_time} onValueChange={(value) => setManualBooking((prev) => ({ ...prev, preferred_time: value }))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder={isLoadingManualTimes ? "Loading times..." : "Select time"} /></SelectTrigger>
                  <SelectContent>{manualAvailableTimes.map((time) => (<SelectItem key={time} value={time}>{time}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="mt-2">
                <input value={manualBooking.notes} onChange={(e) => setManualBooking((prev) => ({ ...prev, notes: e.target.value }))} className="h-9 w-full rounded-md border border-[#ddd] px-3 text-sm" placeholder="Notes (optional)" />
              </div>
              {selectedManualService && (
                <div className="mt-4 rounded-xl border border-[#ececec] bg-white p-3">
                  <p className="text-[11px] uppercase tracking-wide text-[#888] mb-2">Service Add-Ons</p>
                  {manualAvailableAddOns.length === 0 ? (
                    <p className="text-sm text-[#888]">No add-ons for this service.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {manualAvailableAddOns.map((addOn) => {
                        const selected = manualSelectedAddOnIds.includes(addOn.id);
                        return (
                          <label
                            key={addOn.id}
                            className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${
                              selected ? "border-[#111] bg-[#f8f8f8]" : "border-[#e5e5e5]"
                            }`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() =>
                                  setManualSelectedAddOnIds((prev) =>
                                    prev.includes(addOn.id)
                                      ? prev.filter((id) => id !== addOn.id)
                                      : [...prev, addOn.id]
                                  )
                                }
                                className="h-4 w-4 accent-[#111111]"
                              />
                              <span className="truncate">{addOn.name}</span>
                            </div>
                            <span className="text-xs text-[#666] shrink-0">
                              {Number(addOn.price || 0) > 0
                                ? `+$${Number(addOn.price).toFixed(2)}`
                                : "Included"}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-xs text-[#777]">
                  {selectedManualService
                    ? `Total $${manualEstimatedTotal.toFixed(2)} • Due now $${manualDueNow.toFixed(2)} • Remaining $${manualRemaining.toFixed(2)} • ${manualDurationMinutes} min`
                    : "Select a service and time to create the appointment."}
                </p>
                <button onClick={createManualBooking} disabled={isCreatingBooking} className="btn-black text-xs px-4 py-2 rounded-full disabled:opacity-50">{isCreatingBooking ? "Creating..." : "Create Appointment"}</button>
              </div>
            </div>
          </div>
        ) : activeTab === "appointments" ? (
          <div className="glass-card p-8 text-center max-w-3xl mx-auto">
            <p className="text-[#888]">Select a day on the calendar to manage that schedule.</p>
          </div>
        ) : activeTab === "clients" ? (
          <div className="grid gap-4 xl:max-w-[1280px]">
            <section className="glass-card p-4 sm:p-5 overflow-hidden">
              <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#888] mb-2">Client Directory</p>
                  <h3 className="text-lg sm:text-xl font-semibold text-[#111111]">Compact client directory</h3>
                  <p className="text-sm text-[#666] mt-1 max-w-2xl">
                    Search by client name, phone number, or email, then click a card only when you need the full client profile.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 min-w-0 w-full xl:w-auto">
                  <div className="min-w-0 w-full xl:w-[360px]">
                    <label className="relative block">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#777]" />
                      <input
                        value={clientSearchQuery}
                        onChange={(e) => setClientSearchQuery(e.target.value)}
                        className="h-11 w-full rounded-2xl border border-[#ded7cf] bg-[linear-gradient(135deg,#fffaf5,#f6f1eb)] pl-10 pr-4 text-sm outline-none transition focus:border-[#bfa58a] focus:shadow-[0_0_0_4px_rgba(191,165,138,0.12)]"
                        placeholder="Search name, email, or phone"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsClientComposerOpen((prev) => !prev)}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#111111] px-4 py-3 text-sm text-white shadow-[0_14px_28px_rgba(17,17,17,0.12)]"
                  >
                    <UserPlus size={16} />
                    {isClientComposerOpen ? "Close form" : "Add client"}
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-3xl border border-[#ece4da] bg-[linear-gradient(135deg,#fff8f0,#ffffff)] px-4 py-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#9b866f]">Visible Clients</p>
                  <p className="mt-2 text-2xl font-semibold text-[#111111]">{filteredClientBook.length}</p>
                </div>
                <div className="rounded-3xl border border-[#ece4da] bg-[linear-gradient(135deg,#fff8f0,#ffffff)] px-4 py-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#9b866f]">Directory Size</p>
                  <p className="mt-2 text-2xl font-semibold text-[#111111]">{clientBook.length}</p>
                </div>
                <div className="rounded-3xl border border-[#ece4da] bg-[linear-gradient(135deg,#fff8f0,#ffffff)] px-4 py-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#9b866f]">Banned</p>
                  <p className="mt-2 text-2xl font-semibold text-[#111111]">{clientBook.filter((client) => client.is_banned).length}</p>
                </div>
                <div className="rounded-3xl border border-[#ece4da] bg-[linear-gradient(135deg,#fff8f0,#ffffff)] px-4 py-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#9b866f]">Search Mode</p>
                  <p className="mt-2 text-sm font-medium text-[#111111]">
                    {clientSearchQuery.trim() ? `Filtering for "${clientSearchQuery.trim()}"` : "Alphabetical by first name"}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-[#111111] px-3 py-1.5 text-white">
                  {filteredClientBook.length} visible
                </span>
                <span className="rounded-full bg-[#efe7dd] px-3 py-1.5 text-[#6a5643]">
                  {clientBook.length} total clients
                </span>
                {clientSearchQuery.trim() && (
                  <button
                    type="button"
                    onClick={() => setClientSearchQuery("")}
                    className="rounded-full border border-[#ddd] px-3 py-1.5 text-[#555] hover:bg-white"
                  >
                    Clear search
                  </button>
                )}
              </div>

              {isClientComposerOpen && (
                <div className="mt-5 rounded-[28px] border border-[#e7dccf] bg-[linear-gradient(135deg,#fff9f3,#fffdfb)] p-4 sm:p-5 shadow-[0_18px_40px_rgba(17,17,17,0.05)]">
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#9b866f]">New Client</p>
                      <h4 className="text-lg font-semibold text-[#111111] mt-1">Add someone to the client directory</h4>
                    </div>
                    <p className="text-xs text-[#777]">Useful for pre-loading VIP clients or keeping track of offline appointments.</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <input
                      value={newClient.full_name}
                      onChange={(e) => setNewClient((prev) => ({ ...prev, full_name: e.target.value }))}
                      className="h-11 rounded-2xl border border-[#ddd] bg-white px-4 text-sm outline-none focus:border-[#bfa58a]"
                      placeholder="Full name"
                    />
                    <input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient((prev) => ({ ...prev, email: e.target.value }))}
                      className="h-11 rounded-2xl border border-[#ddd] bg-white px-4 text-sm outline-none focus:border-[#bfa58a]"
                      placeholder="Email"
                    />
                    <input
                      value={newClient.phone}
                      onChange={(e) => setNewClient((prev) => ({ ...prev, phone: e.target.value }))}
                      className="h-11 rounded-2xl border border-[#ddd] bg-white px-4 text-sm outline-none focus:border-[#bfa58a]"
                      placeholder="Phone"
                    />
                    <input
                      value={newClient.id_document_name}
                      onChange={(e) => setNewClient((prev) => ({ ...prev, id_document_name: e.target.value }))}
                      className="h-11 rounded-2xl border border-[#ddd] bg-white px-4 text-sm outline-none focus:border-[#bfa58a]"
                      placeholder="ID file name (optional)"
                    />
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={createClientRecord}
                      disabled={isSavingClientDirectory}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#111111] px-4 py-2.5 text-sm text-white disabled:opacity-50"
                    >
                      <UserPlus size={14} />
                      {isSavingClientDirectory ? "Saving..." : "Save client"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setNewClient({ full_name: "", email: "", phone: "", id_document_name: "" });
                        setIsClientComposerOpen(false);
                      }}
                      className="rounded-full border border-[#ddd] px-4 py-2.5 text-sm text-[#555] hover:bg-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>

            {isLoadingClients ? (
              <div className="glass-card p-8 text-center">
                <p className="text-[#888]">Loading client history...</p>
              </div>
            ) : filteredClientBook.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-[#888]">
                  {clientBook.length === 0 ? "No client history yet" : "No clients match that search"}
                </p>
              </div>
            ) : (
              filteredClientBook.map((client) => (
                <div
                  key={client.key}
                  className="overflow-hidden rounded-[30px] border border-[#ebe1d5] bg-[linear-gradient(180deg,rgba(255,251,247,0.98),rgba(255,255,255,0.98))] shadow-[0_18px_44px_rgba(17,17,17,0.05)]"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedClientCard((prev) => (prev === client.key ? null : client.key))}
                    className="w-full px-4 py-4 sm:px-5 sm:py-5 text-left hover:bg-[#fcfaf7] transition-colors"
                  >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0 flex items-start gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#111111,#5b5147)] text-sm font-semibold text-white">
                          {String(client.name || "?").trim().slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-[#111111] text-sm sm:text-base">{client.name}</p>
                            {client.is_banned && (
                              <span className="rounded-full bg-[#fff0ef] px-2.5 py-1 text-[10px] uppercase tracking-wide text-[#b14434] border border-[#f2c6c0]">
                                Banned
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#888] break-all sm:truncate">{client.email || "No email saved"}</p>
                          <p className="text-sm text-[#888]">{client.phone || "No phone saved"}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full bg-white px-3 py-1.5 text-[#444] border border-[#e8dfd4]">
                          {client.visits} visits
                        </span>
                        <span className="rounded-full bg-white px-3 py-1.5 text-[#444] border border-[#e8dfd4]">
                          Last {client.latest ? format(client.latest, "MMM d, yyyy") : "N/A"}
                        </span>
                        <span className="rounded-full bg-white px-3 py-1.5 text-[#444] border border-[#e8dfd4]">
                          {expandedClientCard === client.key ? "Hide details" : "View profile"}
                        </span>
                      </div>
                    </div>
                  </button>

                  {expandedClientCard === client.key && (
                    <div className="border-t border-[#f0e6da] px-4 pb-4 sm:px-5 sm:pb-5">
                      <div className="grid gap-4 pt-4 xl:grid-cols-[1.05fr_0.95fr]">
                        <div className="space-y-4">
                          <div className="rounded-3xl border border-[#ece7e0] bg-white p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                              <p className="text-xs uppercase tracking-wide text-[#888]">Client Snapshot</p>
                              <span className="text-[11px] text-[#999] break-all">
                                {client.id_document_name ? client.id_document_name : "No ID on file"}
                              </span>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2 text-sm">
                              <div className="rounded-2xl bg-[#faf7f2] p-3">
                                <p className="text-[11px] uppercase tracking-wide text-[#999] mb-1">Email</p>
                                <p className="text-[#333] break-all">{client.email || "Not provided"}</p>
                              </div>
                              <div className="rounded-2xl bg-[#faf7f2] p-3">
                                <p className="text-[11px] uppercase tracking-wide text-[#999] mb-1">Phone</p>
                                <p className="text-[#333] break-all">{client.phone || "Not provided"}</p>
                              </div>
                              <div className="rounded-2xl bg-[#faf7f2] p-3">
                                <p className="text-[11px] uppercase tracking-wide text-[#999] mb-1">Upcoming</p>
                                <p className="text-[#333] break-words">{client.upcoming ? format(client.upcoming, "MMM d, yyyy h:mm a") : "None scheduled"}</p>
                              </div>
                              <div className="rounded-2xl bg-[#faf7f2] p-3">
                                <p className="text-[11px] uppercase tracking-wide text-[#999] mb-1">Status</p>
                                <p className="text-[#333] break-words">
                                  {client.is_banned
                                    ? `Banned${client.banned_at ? ` • ${format(client.banned_at, "MMM d, yyyy")}` : ""}`
                                    : "Allowed to book"}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-3xl border border-[#ece7e0] bg-white p-4">
                            <p className="text-xs uppercase tracking-wide text-[#888] mb-3">Recent Services</p>
                            <div className="space-y-2">
                              {client.history.length === 0 ? (
                                <p className="text-sm text-[#888]">No service history yet</p>
                              ) : (
                                client.history.map((h) => (
                                  <div key={h.id} className="rounded-2xl bg-[#faf7f2] px-3 py-3 text-sm text-[#555]">
                                    <p className="break-words font-medium text-[#333]">{h.service}</p>
                                    <p className="text-xs text-[#8d8174] mt-1">
                                      {h.date ? format(h.date, "MMM d, yyyy") : "No date"}
                                    </p>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="rounded-3xl border border-[#ece7e0] bg-white p-4">
                            <p className="text-xs uppercase tracking-wide text-[#888] mb-2">Stylist Notes</p>
                            <textarea
                              value={clientNotes[client.key] || ""}
                              onChange={(e) =>
                                setClientNotes((prev) => ({ ...prev, [client.key]: e.target.value }))
                              }
                              className="w-full min-h-[130px] resize-y rounded-2xl border border-[#ddd] px-3 py-3 text-sm outline-none focus:border-[#bfa58a]"
                              placeholder="Add client preferences, allergies, style notes, or appointment reminders..."
                            />
                            <button
                              onClick={() => saveClientNote(client.email, client.phone)}
                              className="mt-3 inline-flex w-full sm:w-auto items-center justify-center gap-1 px-4 py-2.5 rounded-full bg-[#111111] text-white text-xs"
                              disabled={savingClientKey === client.key}
                            >
                              <Save size={12} />
                              {savingClientKey === client.key ? "Saving..." : "Save Note"}
                            </button>
                          </div>

                          <div className="rounded-3xl border border-[#ece7e0] bg-white p-4">
                            <p className="text-xs uppercase tracking-wide text-[#888] mb-3">Client Controls</p>
                            {client.is_banned && client.ban_reason && (
                              <div className="mb-3 rounded-2xl border border-[#f2d0ca] bg-[#fff5f3] px-3 py-3 text-sm text-[#7b433a]">
                                <span className="font-medium">Ban reason:</span> {client.ban_reason}
                              </div>
                            )}
                            {!client.is_persisted && (
                              <div className="mb-3 rounded-2xl border border-[#ece4da] bg-[#faf7f2] px-3 py-3 text-sm text-[#6f5d4c]">
                                This profile is being shown from booking history only. Banning will create a managed client record. Removing is only available for saved client records.
                              </div>
                            )}
                            <div className="grid gap-2">
                              <button
                                type="button"
                                onClick={() => setClientBanStatus(client, !client.is_banned)}
                                disabled={isSavingClientDirectory}
                                className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm transition-colors disabled:opacity-50 ${
                                  client.is_banned
                                    ? "border border-[#c9d9c4] bg-[#f6fbf4] text-[#2c5c34] hover:bg-[#eef8eb]"
                                    : "border border-[#f0c8c4] bg-[#fff4f3] text-[#a33e31] hover:bg-[#ffeded]"
                                }`}
                              >
                                <Ban size={14} />
                                {client.is_banned ? "Restore booking access" : "Ban from booking"}
                              </button>

                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button
                                    type="button"
                                    disabled={isSavingClientDirectory || !client.is_persisted}
                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#ead5d3] bg-white px-4 py-2.5 text-sm text-[#b14434] hover:bg-[#fff5f3] disabled:opacity-50"
                                  >
                                    <Trash2 size={14} />
                                    {client.is_persisted ? "Remove client" : "Saved records only"}
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove this client?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This removes {client.name} from the client directory and deletes the saved stylist note for this client.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteClientRecord(client)}
                                      className="bg-red-500 hover:bg-red-600"
                                    >
                                      Remove
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : activeTab === "availability" ? (
          <div className="space-y-5">
            <section className="grid gap-5 xl:grid-cols-[1.15fr_0.85fr]">
              <div className="glass-card p-5 bg-[linear-gradient(180deg,rgba(255,250,244,0.96),rgba(255,255,255,0.98))]">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#8f7d69] mb-2">Hours Of Operation</p>
                    <h3 className="text-xl font-semibold text-[#111111]">Control when clients can book</h3>
                    <p className="text-sm text-[#666] mt-1 max-w-2xl">
                      Update operating hours, notice window, and booking slot cadence without touching code.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:min-w-[290px]">
                    <div className="rounded-2xl border border-[#eadfd4] bg-white px-3 py-3">
                      <p className="uppercase tracking-wide text-[#a1886f]">Booking Notice</p>
                      <p className="mt-1 text-lg font-semibold text-[#111111]">{availabilitySettings.min_notice_hours}h</p>
                    </div>
                    <div className="rounded-2xl border border-[#eadfd4] bg-white px-3 py-3">
                      <p className="uppercase tracking-wide text-[#a1886f]">Slot Interval</p>
                      <p className="mt-1 text-lg font-semibold text-[#111111]">{availabilitySettings.slot_interval_minutes} min</p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-3 mb-5">
                  <div className="rounded-2xl border border-[#ece4da] bg-white p-4">
                    <label className="text-xs uppercase tracking-wide text-[#888] block mb-2">Minimum Booking Notice</label>
                    <Select
                      value={String(availabilitySettings.min_notice_hours)}
                      onValueChange={(value) =>
                        setAvailabilitySettings((prev) => ({ ...prev, min_notice_hours: Number(value) }))
                      }
                    >
                      <SelectTrigger className="h-11 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[12, 24, 36, 48, 72].map((hours) => (
                          <SelectItem key={hours} value={String(hours)}>
                            {hours} hours
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-2xl border border-[#ece4da] bg-white p-4">
                    <label className="text-xs uppercase tracking-wide text-[#888] block mb-2">Booking Slot Interval</label>
                    <Select
                      value={String(availabilitySettings.slot_interval_minutes)}
                      onValueChange={(value) =>
                        setAvailabilitySettings((prev) => ({ ...prev, slot_interval_minutes: Number(value) }))
                      }
                    >
                      <SelectTrigger className="h-11 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[15, 30, 60].map((minutes) => (
                          <SelectItem key={minutes} value={String(minutes)}>
                            {minutes} minutes
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-2xl border border-[#ece4da] bg-white p-4">
                    <label className="text-xs uppercase tracking-wide text-[#888] block mb-2">Max Booking Window</label>
                    <Select
                      value={String(availabilitySettings.max_advance_days)}
                      onValueChange={(value) =>
                        setAvailabilitySettings((prev) => ({ ...prev, max_advance_days: Number(value) }))
                      }
                    >
                      <SelectTrigger className="h-11 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[30, 60, 90, 120, 180].map((days) => (
                          <SelectItem key={days} value={String(days)}>
                            {days} days
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  {availabilitySettings.weekly_hours.map((day) => (
                    <div
                      key={day.day_key}
                      className="rounded-[24px] border border-[#ece4da] bg-white p-4 shadow-[0_10px_30px_rgba(17,17,17,0.03)]"
                    >
                      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                        <div className="min-w-0">
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => updateAvailabilityDay(day.day_key, { enabled: !day.enabled })}
                              className={`relative h-7 w-12 rounded-full transition-colors ${day.enabled ? "bg-[#111111]" : "bg-[#d7d1ca]"}`}
                              aria-label={`Toggle ${day.label}`}
                            >
                              <span
                                className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-transform ${day.enabled ? "translate-x-6" : "translate-x-1"}`}
                              />
                            </button>
                            <div>
                              <p className="font-medium text-[#111111]">{day.label}</p>
                              <p className="text-xs text-[#888]">{day.enabled ? "Open for bookings" : "Closed all day"}</p>
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,180px)_minmax(0,180px)] xl:min-w-[380px]">
                          <div>
                            <label className="text-[11px] uppercase tracking-wide text-[#999] block mb-1.5">Open</label>
                            <Select
                              value={day.start_time}
                              onValueChange={(value) => updateAvailabilityDay(day.day_key, { start_time: value })}
                              disabled={!day.enabled}
                            >
                              <SelectTrigger className="h-10 w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {operatingTimeOptions.map((time) => (
                                  <SelectItem key={`${day.day_key}-start-${time}`} value={time}>
                                    {time}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-[11px] uppercase tracking-wide text-[#999] block mb-1.5">Close</label>
                            <Select
                              value={day.end_time}
                              onValueChange={(value) => updateAvailabilityDay(day.day_key, { end_time: value })}
                              disabled={!day.enabled}
                            >
                              <SelectTrigger className="h-10 w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {operatingTimeOptions.map((time) => (
                                  <SelectItem key={`${day.day_key}-end-${time}`} value={time}>
                                    {time}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-xs text-[#777]">
                    These settings control your public booking availability and the admin calendar time generation.
                  </p>
                  <button
                    type="button"
                    onClick={saveAvailabilitySettings}
                    disabled={isSavingAvailabilitySettings}
                    className="btn-black text-sm px-5 py-2.5 rounded-full disabled:opacity-50"
                  >
                    {isSavingAvailabilitySettings ? "Saving..." : "Save Hours & Booking Rules"}
                  </button>
                </div>
              </div>

              <div className="space-y-5">
                <section className="glass-card p-5">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8f7d69] mb-2">Date Controls</p>
                  <h3 className="text-lg font-semibold text-[#111111]">Block Off Time</h3>
                  <p className="text-sm text-[#666] mt-1 mb-4">
                    Selected date: {format(selectedCalendarDate, "EEEE, MMMM d, yyyy")}
                  </p>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      onClick={() => createAvailabilityBlock({ block_type: "day", date: selectedDayLabel })}
                      disabled={isSavingBlock}
                      className="btn-black text-sm disabled:opacity-50"
                    >
                      Block Day
                    </button>
                    <button
                      onClick={() =>
                        createAvailabilityBlock({
                          block_type: "week",
                          start_date: selectedWeekStart,
                          end_date: selectedWeekEnd,
                        })
                      }
                      disabled={isSavingBlock}
                      className="btn-black text-sm disabled:opacity-50"
                    >
                      Block Week
                    </button>
                    <button
                      onClick={() => createAvailabilityBlock({ block_type: "month", month: selectedMonth })}
                      disabled={isSavingBlock}
                      className="btn-black text-sm disabled:opacity-50"
                    >
                      Block Month
                    </button>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                      <Select value={selectedBlockTime} onValueChange={setSelectedBlockTime}>
                        <SelectTrigger className="h-10 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button
                        onClick={() =>
                          createAvailabilityBlock({
                            block_type: "time",
                            date: selectedDayLabel,
                            time: selectedBlockTime,
                          })
                        }
                        disabled={isSavingBlock}
                        className="btn-black text-xs px-4 disabled:opacity-50"
                      >
                        Block
                      </button>
                    </div>
                  </div>
                </section>

                <section className="glass-card p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.22em] text-[#8f7d69]">Current Blocks</p>
                      <h3 className="text-lg font-semibold text-[#111111] mt-1">Existing closures</h3>
                    </div>
                    <span className="rounded-full bg-[#111111] px-3 py-1.5 text-xs text-white">
                      {safeAvailabilityBlocks.length}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                    {isLoadingAvailability ? (
                      <p className="text-sm text-[#888]">Loading availability...</p>
                    ) : safeAvailabilityBlocks.length === 0 ? (
                      <p className="text-sm text-[#888]">No blocked dates or times.</p>
                    ) : (
                      safeAvailabilityBlocks.map((block) => (
                        <div key={block.id} className="rounded-2xl border border-[#ececec] bg-white p-3 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-wide text-[#8f7d69]">{block.block_type}</p>
                            <p className="text-sm text-[#333] break-words">
                              {block.block_type === "day" && block.date}
                              {block.block_type === "week" && `${block.start_date} to ${block.end_date}`}
                              {block.block_type === "month" && block.month}
                              {block.block_type === "time" && `${block.date} at ${block.time}`}
                            </p>
                          </div>
                          <button
                            onClick={() => removeAvailabilityBlock(block.id)}
                            className="text-xs px-3 py-1.5 rounded-full border border-[#ddd] hover:bg-[#f7f7f7]"
                          >
                            Open
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              </div>
            </section>
          </div>
        ) : activeTab === "website" ? (
          <div className="space-y-5">
            <section className="grid gap-5 xl:grid-cols-[1.05fr_0.95fr]">
              <div className="glass-card p-5 bg-[linear-gradient(180deg,rgba(255,250,244,0.96),rgba(255,255,255,0.98))]">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#8f7d69] mb-2">Website Control</p>
                <h3 className="text-xl font-semibold text-[#111111]">Homepage Announcement Banner</h3>
                <p className="text-sm text-[#666] mt-1 mb-5 max-w-2xl">
                  Update the top banner message anytime to announce calendar openings, specials, closures, or important reminders.
                </p>

                <div className="rounded-[26px] border border-[#e8ddd1] bg-[linear-gradient(135deg,#111111,#3f3329)] p-5 text-white shadow-[0_20px_50px_rgba(17,17,17,0.12)]">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-white/60 mb-2">Live Preview</p>
                  <div className="announcement-banner rounded-2xl py-3 text-center">
                    <p className="text-sm tracking-wide">{siteSettings.announcement_banner_text}</p>
                  </div>
                </div>

                <div className="mt-5">
                  <label className="text-xs uppercase tracking-wide text-[#888] block mb-2">Banner Message</label>
                  <textarea
                    value={siteSettings.announcement_banner_text}
                    onChange={(e) =>
                      setSiteSettings((prev) => ({ ...prev, announcement_banner_text: e.target.value }))
                    }
                    className="w-full min-h-[120px] resize-y rounded-2xl border border-[#ddd] px-4 py-3 text-sm outline-none focus:border-[#bfa58a] focus:shadow-[0_0_0_4px_rgba(191,165,138,0.12)]"
                    placeholder="Ex: June Calendar Now Open!"
                  />
                </div>

                <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-xs text-[#777]">
                    Keep it short and clear so it reads well across mobile and desktop.
                  </p>
                  <button
                    type="button"
                    onClick={saveSiteSettings}
                    disabled={isSavingSiteSettings}
                    className="btn-black text-sm px-5 py-2.5 rounded-full disabled:opacity-50"
                  >
                    {isSavingSiteSettings ? "Saving..." : "Save Banner"}
                  </button>
                </div>
              </div>

              <div className="space-y-5">
                <section className="glass-card p-5">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8f7d69] mb-2">Why This Helps</p>
                  <h3 className="text-lg font-semibold text-[#111111]">More control, fewer edits</h3>
                  <div className="mt-4 space-y-3 text-sm text-[#555]">
                    <div className="rounded-2xl border border-[#ece4da] bg-white px-4 py-3">
                      <p className="font-medium text-[#111111]">Calendar launches</p>
                      <p className="mt-1">Post “May Calendar Now Open” or close booking windows without asking for a code change.</p>
                    </div>
                    <div className="rounded-2xl border border-[#ece4da] bg-white px-4 py-3">
                      <p className="font-medium text-[#111111]">Flash updates</p>
                      <p className="mt-1">Share squeeze-in days, holiday closures, deposit reminders, or travel notices instantly.</p>
                    </div>
                    <div className="rounded-2xl border border-[#ece4da] bg-white px-4 py-3">
                      <p className="font-medium text-[#111111]">Client communication</p>
                      <p className="mt-1">Use the banner as the first place clients see important changes before they start booking.</p>
                    </div>
                  </div>
                </section>
              </div>
            </section>
          </div>
        ) : (
          <div data-testid="contacts-list" className="space-y-3">
            {isLoadingContacts ? (
              <div className="glass-card p-8 text-center">
                <p className="text-[#888]">Loading messages...</p>
              </div>
            ) : contacts.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-[#888]">No messages yet</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.id}
                  data-testid={`contact-${contact.id}`}
                  className="glass-card p-4"
                >
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <div>
                      <p className="font-medium">{contact.name}</p>
                      <p className="text-sm text-[#888]">{contact.email}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <p className="text-xs text-[#888]">
                        {format(new Date(contact.created_at), "MMM d, yyyy")}
                      </p>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            type="button"
                            disabled={deletingContactId === contact.id}
                            data-testid={`delete-contact-${contact.id}`}
                            aria-label={`Delete message from ${contact.name}`}
                            className="inline-flex items-center justify-center rounded-full border border-[#ead5d3] bg-white p-2 text-[#b14434] transition-colors hover:bg-[#fff5f3] disabled:opacity-50"
                          >
                            <Trash2 size={14} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently removes the message from {contact.name}. It cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteContactMessage(contact)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                  <p className="text-sm text-[#666]">{contact.message}</p>
                  {contact.phone && (
                    <p className="text-sm text-[#888] mt-2">Phone: {contact.phone}</p>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {!isLoading && activeTab === "appointments" && upcomingBookings.length > 0 && (
          <section className="mt-8 glass-card p-4">
            <h3 className="font-medium mb-3">Upcoming Appointments</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {upcomingBookings.slice(0, isMobileViewport ? 4 : 8).map((booking) => (
                <div
                  key={`up-${booking.id}`}
                  draggable
                  onDragStart={() => setDraggedBookingId(booking.id)}
                  className="rounded-xl border border-[#ececec] bg-white p-3 cursor-grab active:cursor-grabbing"
                >
                  <p className="font-medium text-sm">{booking.full_name}</p>
                  <p className="text-xs text-[#888]">{booking.service_name}</p>
                  <p className="text-xs text-[#666] mt-1">
                    {booking.scheduledAt ? format(booking.scheduledAt, "EEE, MMM d • h:mm a") : "Schedule pending"}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
